/**
 * IMPORTANT: This file is used by BOTH react and node scripts. Be careful when importing not to add any scripts that will
 * import frontend only packages or functions or cause circular imports. If you must, look at how the filters are imported
 * for `FeatureCollectionInBounds` and other properties. Managing imports like this allows the node scripts to run without
 * frontend dependencies.
 *
 * Example:
 * We have a node script that runs to sync these layers with our marketign site. We don't need the filters defined on the layers
 * but if we try to import them we start importing all of our other geo dependencies and other files which may also import
 * this file. Because of this, we define placeholder constants and functions, and conditionally initialize them when the script runs.
 */

import * as geojson from 'geojson';
import type {ImmutableOf} from 'immutable';
import {sortBy} from 'lodash';
import React from 'react';

import type {DataRange} from 'app/components/AnalyzePolygonChart/types';
import type {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import colors from 'app/styles/colors.json';

import {isWhite} from './colorUtils';
import {
  CDL_LANDCOVER_VALUE_MAP,
  CHLORIS_FOREST_VALUE_MAP,
  ESA_WORLDCOVER_VALUE_MAP,
  GLOBAL_FOREST_CHANGE_LOSSYEAR_VALUE_MAP,
  GLOBAL_FOREST_COVER_FORESTCOVER_VALUE_MAP,
  GNATSGO_SOILTYPE_VALUE_MAP,
  GRASSLANDS_RISK_VALUE_MAP,
  IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER_VALUE_MAP,
  IMPACT_OBSERVATORY_LANDCOVER_VALUE_MAP,
  LENS_TIERS_TYPE,
  MAPBIOMAS_BRAZIL_LANDCOVER_VALUE_MAP,
  MAPBIOMAS_CHACO_LANDCOVER_VALUE_MAP,
  MAPBIOMAS_COLOMBIA_LANDCOVER_VALUE_MAP,
  NLCD_ANNUAL_LANDCOVER_VALUE_MAP,
  NLCD_LANDCOVER_VALUE_MAP,
  RCMAP_WESTERN_NA_BBOX,
  SBTN_NATURAL_LANDS_CLASSIFICATION_VALUE_MAP,
  SOURCE_DETAILS_BY_ID,
  SPACE_INTELLIGENCE_HABITAT_10_CLASS_VALUE_MAP,
  SPACE_INTELLIGENCE_HABITAT_URBAN_VALUE_MAP,
  SPACE_INTELLIGENCE_HABITAT_VALUE_MAP,
  TMF_ANNUAL_CHANGE_VALUE_MAP,
  WLFW_CO_ISI_VALUE_MAP,
  WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER_VALUE_MAP,
} from './constants';
import {BRAZIL_SHAPE, CHACO_BBOX, COLOMBIA_SHAPE, COLORADO_SHAPE} from './geoJsonUtils';
import {
  AG_NDTI_GRADIENT_STOPS,
  BURN_GRADIENT_STOPS,
  CHLORIS_BIOMASS_STOCK_STOPS,
  COOL_GRADIENT_STOPS,
  COPPER_GRADIENT_STOPS,
  DEFAULT_GRADIENT_STOPS,
  GNBU_REVERSE_GRADIENT_STOPS,
  GREENS_GRADIENT_STOPS,
  GradientStops,
  INVASIVE_GRASS_PERCENT_COVER_GRADIENT_STOPS,
  MAGMA_GRADIENT_STOPS,
  NDVI_GRADIENT_STOPS,
  NLCD_ANNUAL_FIS_GRADIENT_STOPS,
  PLANET_FOREST_CARBON_CANOPY_COVER_STOPS,
  PLANET_FOREST_CARBON_CANOPY_HEIGHT_STOPS,
  RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  RDBU_R_GRADIENT_STOPS,
  S1_WATER_GRADIENT_STOPS,
  S2_NDWI_GRADIENT_STOPS,
  SALO_GREEN_VEGETATION_GRADIENT_STOPS,
  SEISMIC_R_GRADIENT_STOPS,
  SPACE_INTELLIGENCE_ABOVEGROUND_CARBON_GRADIENT_STOPS,
  VIRIDIS_GRADIENT_STOPS,
  YLGN_GRADIENT_STOPS,
} from './gradientStops';
// Instead of direct imports, declare function types
let _featureCollectionInBounds: any = null;
let _featureCollectionInUS: any = null;

// Only import featureCollectionUtils if we're in a browser/React context
if (typeof window !== 'undefined') {
  // In a browser/React context
  const utils = require('./featureCollectionUtils');

  if (!utils) {
    throw new Error('Failed to load utils functions for layers');
  }

  _featureCollectionInBounds = utils.featureCollectionInBounds;
  _featureCollectionInUS = utils.featureCollectionInUS;
}

export const UPSTREAM_RASTER_PREFIX = 'FeatureRaster';

export const NONE = 'none';
// We use this new “ANY” synthetic layer key because it specifically does not
// come up in the API responses. This is a way of saying “we want high-res
// truecolor, but the source is currently undetermined.” This state is relevant
// during app startup, for example, before FeatureData has loaded and we have a
// specific high-res truecolor scene we’re showing.

export const ANY_TRUECOLOR_HIGH_RES = 'ANY_high-res-truecolor';
// TODO(emily): Delete after we’ve updated feature data fixtures (#5576) and
// consequently no longer use this layer in AnalyzePolygonTool.stories.tsx.
export const ALL_MNDWI = 'ALL_MNDWIw';

export const S2_NDVI = 'S2_NDVI';
export const S2_EVI = 'S2_EVI';
export const S2_NDCI = 'S2_NDCI';
export const S2_PHYCOCYANIN = 'S2_Phycocyanin';
export const S2_RRER = 'S2_RRER';
export const S2_BGRI = 'S2_BGRI';
export const S2_TBI = 'S2_TBI';
export const S2_NDWI = 'S2_NDWIw';
export const S2_NDWI_MOISTURE = 'S2_NDWIw_moisture';
export const S2_NDWI_M = 'S2_NDWIm';
export const S2_NDSI = 'S2_NDSI';
export const S2_NBR = 'S2_NBR';
export const S2_NDBI = 'S2_NDBI';
export const S2_NDTI = 'S2_NDTI'; // turbidity
export const S2_AgNDTI = 'S2_AgNDTI'; // tillage
export const S2_TRUECOLOR = 'S2_truecolor';
export const S2_FALSECOLOR_SWIR_NIR_GREEN = 'S2_falsecolor_SWIR1_N_G';
export const S2_FALSECOLOR_BLUE_SWIR = 'S2_falsecolor_B_SWIR1_SWIR2';
export const S2_FALSECOLOR_NIR_RED_GREEN = 'S2_falsecolor_N_R_G';

export const L8_NDVI = 'L8_NDVI';
export const L8_NDWI = 'L8_NDWIw';
export const L8_NDSI = 'L8_NDSI';
export const L8_NBR = 'L8_NBR';
export const L8_NDBI = 'L8_NDBI';
export const L8_TRUECOLOR = 'L8_truecolor';
export const L8_FALSECOLOR_SWIR_NIR_GREEN = 'L8_falsecolor_SWIR1_N_G';
export const L8_FALSECOLOR_BLUE_SWIR = 'L8_falsecolor_B_SWIR1_SWIR2';
export const L8_FALSECOLOR_NIR_RED_GREEN = 'L8_falsecolor_N_R_G';

export const HLS_NDVI = 'HLS_NDVI';
export const HLS_TRUECOLOR = 'HLS_truecolor';

export const S1_WATER_CLASSIFICATION = 'S1_RADAR-WATER';

export const CDL_LANDCOVER = 'CDL_landcover';
export const NLCD_LANDCOVER = 'NLCD_landcover';
export const NLCD_ANNUAL_LANDCOVER = 'NLCD-ANNUAL_landcover';
export const NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE = 'NLCD-ANNUAL-FIS_FIS';
export const EAG_PERCENT_COVER = 'USGS-EAG_EAG';
export const CHEATGRASS_PERCENT_COVER = 'USGS-CHEATGRASS_cheatgrass';
// USGS product page for the weekly RCMAP components, linked from every one of their descriptions.
export const RCMAP_INFO_URL =
  'https://www.mrlc.gov/data/type/rcmap-weekly-herbaceous-and-exotic-annual-grass';

// Each key's suffix must match a band name rcmap_weekly writes, since that name is also the
// imagery config key that selects the band. See backend workers/tile/creators/rcmap_weekly.py.
export const RCMAP_TOTAL_HERBACEOUS = 'RCMAP-WEEKLY_total_herbaceous';
export const RCMAP_GREEN_HERBACEOUS = 'RCMAP-WEEKLY_green_herbaceous';
export const RCMAP_SENESCED_HERBACEOUS = 'RCMAP-WEEKLY_senesced_herbaceous';
export const RCMAP_TOTAL_EAG = 'RCMAP-WEEKLY_total_eag';
export const RCMAP_CHEATGRASS = 'RCMAP-WEEKLY_cheatgrass';
export const RCMAP_FIELD_BROME = 'RCMAP-WEEKLY_fieldbrome';
export const RCMAP_MEDUSAHEAD = 'RCMAP-WEEKLY_medusahead';
export const RCMAP_SANDBERGS_BLUEGRASS = 'RCMAP-WEEKLY_sandbergs_bluegrass';
export const ESA_BIOMASS_STOCK = 'ESA-BIOMASS_AGB';
export const ESA_WORLDCOVER_LANDCOVER = 'ESA-WORLDCOVER_landcover';
export const IMPACT_OBSERVATORY_LANDCOVER = 'IMPACT-OBSERVATORY-LANDCOVER_landcover';
export const IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER =
  'IMPACT-OBSERVATORY-HIGH-RES-LANDCOVER_landcover';
export const IMPACT_OBSERVATORY_BIODIVERSITY =
  'IMPACT-OBSERVATORY-BIODIVERSITY_biodiversity_intactness';
export const MAPBIOMAS_BRAZIL_LANDCOVER = 'MAPBIOMAS-BRAZIL-LANDCOVER_landcover';
export const MAPBIOMAS_CHACO_LANDCOVER = 'MAPBIOMAS-CHACO-LANDCOVER_landcover';
export const MAPBIOMAS_COLOMBIA_LANDCOVER = 'MAPBIOMAS-COLOMBIA-LANDCOVER_landcover';
export const SBTN_NATURAL_LANDCOVER = 'SBTN-WRINLC_landcover';
export const TMF_ANNUAL_CHANGE_LANDCOVER = 'TMF-ANNUAL-CHANGE_landcover';
export const GNATSGO_SOILTYPE = 'GNATSGO-HYDGRPDCD_soiltype';

export const NAIP_HIGH_RES_TRUECOLOR = 'NAIP_high-res-truecolor';
export const NAIP_NDVI = 'NAIP_NDVI';
export const NAIP_NDWI = 'NAIP_NDWIw';
export const NAIP_NDWI_MOISTURE = 'NAIP_NDWIw_moisture';
export const NAIP_FALSECOLOR_NIR_RED_GREEN = 'NAIP_falsecolor_N_R_G';

export const METAWRI_CANOPY_HEIGHT = 'METAWRI-CANOPY-HEIGHT_CanopyHeight';
export const WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER = 'WRI-GPWGGC_landcover';
export const GRASSLANDS_RISK = 'GRASSLANDS-RISK_grasslands_risk';
export const CTREES_TREE_HEIGHT = 'CTREES-TREE-HEIGHT_tree_height';
export const WLFW_CO_ISI = 'WLFW-CO-ISI_isi';

export const PLANET_FOREST_ABOVEGROUND_CARBON = 'PLANET-FOREST-CARBON-30_abovegroundcarbon';
export const PLANET_FOREST_CANOPY_HEIGHT = 'PLANET-FOREST-CARBON-30_canopyheight';
export const PLANET_FOREST_CANOPY_COVER = 'PLANET-FOREST-CARBON-30_canopycover';
export const PLANET_FOREST_ABOVEGROUND_CARBON_3M = 'PLANET-FOREST-CARBON-3_abovegroundcarbon';
export const PLANET_FOREST_CANOPY_COVER_3M = 'PLANET-FOREST-CARBON-3_canopycover';
export const PLANET_FOREST_CANOPY_HEIGHT_3M = 'PLANET-FOREST-CARBON-3_canopyheight';

export const CHLORIS_30_BIOMASS_STOCK = 'CHLORIS-30_stock';
export const CHLORIS_30_BIOMASS_STOCK_CHANGE = 'CHLORIS-30_change';
export const CHLORIS_30_FOREST_COVER = 'CHLORIS-30_forest';
export const CHLORIS_10_BIOMASS_STOCK = 'CHLORIS-10_stock';
export const CHLORIS_10_BIOMASS_STOCK_CHANGE = 'CHLORIS-10_change';
export const CHLORIS_10_FOREST_COVER = 'CHLORIS-10_forest';
// These are not real layers - but used by the layersLibrary to group together the above
// Planet forest carbon layers that can only be turned on/off as a group

export const PLANET_FOREST_DILIGENCE_PACKAGE = 'PLANET-FOREST-CARBON-30_package';
export const PLANET_FOREST_MONITORING_PACKAGE = 'PLANET-FOREST-CARBON-3_package';
export const CHLORIS_30_PACKAGE = 'CHLORIS-30_package';
export const CHLORIS_10_PACKAGE = 'CHLORIS-10_package';

export const PRISM_M_PRECIPITATION = 'PRISM-M_precipitation';
export const GEFS_ANALYSIS_MONTHLY_PRECIPITATION = 'GEFS-ANALYSIS-MONTHLY_precipitation';
export const GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN = 'GEFS-ANALYSIS-MONTHLY_temperature_mean';
// These layers are used for Lens for Carbon

export const SALO_ABOVEGROUND_CARBON_30M = 'SALO_abovegroundcarbon';
export const SALO_CANOPY_COVER_30M = 'SALO_canopycover';
export const SALO_CANOPY_HEIGHT_30M = 'SALO_canopyheight';
export const SALO_ABOVEGROUND_CARBON_3M = 'SALO_abovegroundcarbon-03';
export const SALO_CANOPY_COVER_3M = 'SALO_canopycover-03';
export const SALO_CANOPY_HEIGHT_3M = 'SALO_canopyheight-03';
export const SPACE_INTELLIGENCE_CARBON = 'SPACE-INTELLIGENCE_Carbon';
export const SPACE_INTELLIGENCE_CARBON_TREE_CANOPY = 'SPACE-INTELLIGENCE_CarbonTreeCanopy';
export const SPACE_INTELLIGENCE_HABITAT = 'SPACE-INTELLIGENCE_Habitat';
export const SPACE_INTELLIGENCE_HABITAT_URBAN = 'SPACE-INTELLIGENCE_HabitatUrban';
export const SPACE_INTELLIGENCE_HABITAT_10_CLASS = 'SPACE-INTELLIGENCE_Habitat10Class';

// Airbus Definitions
// PNEO
export const AIRBUS_PNEO_HIGH_RES_TRUECOLOR = 'AIRBUS-PNEO_high-res-truecolor';
export const AIRBUS_PNEO_MULTISPECTRAL_NDVI = 'AIRBUS-PNEO-MULTISPECTRAL_NDVI';
export const AIRBUS_PNEO_MULTISPECTRAL_NDWI = 'AIRBUS-PNEO-MULTISPECTRAL_NDWIw';
export const AIRBUS_PNEO_MULTISPECTRAL_NDWI_MOISTURE = 'AIRBUS-PNEO-MULTISPECTRAL_NDWIw_moisture';
export const AIRBUS_PNEO_ARCHIVE_HIGH_RES_TRUECOLOR = 'AIRBUS-PNEO-ARCHIVE_high-res-truecolor';
export const AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDVI = 'AIRBUS-PNEO-ARCHIVE-MULTISPECTRAL_NDVI';
export const AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDWI = 'AIRBUS-PNEO-ARCHIVE-MULTISPECTRAL_NDWIw';
export const AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE =
  'AIRBUS-PNEO-ARCHIVE-MULTISPECTRAL_NDWIw_moisture';

// PLEIADES
export const AIRBUS_PLEIADES_HIGH_RES_TRUECOLOR = 'AIRBUS-PLEIADES_high-res-truecolor';
export const AIRBUS_PLEIADES_MULTISPECTRAL_NDVI = 'AIRBUS-PLEIADES-MULTISPECTRAL_NDVI';
export const AIRBUS_PLEIADES_MULTISPECTRAL_NDWI = 'AIRBUS-PLEIADES-MULTISPECTRAL_NDWIw';
export const AIRBUS_PLEIADES_MULTISPECTRAL_NDWI_MOISTURE =
  'AIRBUS-PLEIADES-MULTISPECTRAL_NDWIw_moisture';
export const AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDVI =
  'AIRBUS-PLEIADES-ARCHIVE-MULTISPECTRAL_NDVI';
export const AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI =
  'AIRBUS-PLEIADES-ARCHIVE-MULTISPECTRAL_NDWIw';
export const AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE =
  'AIRBUS-PLEIADES-ARCHIVE-MULTISPECTRAL_NDWIw_moisture';
export const AIRBUS_PLEIADES_ARCHIVE_HIGH_RES_TRUECOLOR =
  'AIRBUS-PLEIADES-ARCHIVE_high-res-truecolor';

// SPOT
export const AIRBUS_SPOT_HIGH_RES_TRUECOLOR = 'AIRBUS-SPOT_high-res-truecolor';
export const AIRBUS_SPOT_ARCHIVE_HIGH_RES_TRUECOLOR = 'AIRBUS-SPOT-ARCHIVE_high-res-truecolor';
export const AIRBUS_SPOT_MULTISPECTRAL_NDVI = 'AIRBUS-SPOT-MULTISPECTRAL_NDVI';
export const AIRBUS_SPOT_MULTISPECTRAL_NDWI = 'AIRBUS-SPOT-MULTISPECTRAL_NDWIw';
export const AIRBUS_SPOT_MULTISPECTRAL_NDWI_MOISTURE = 'AIRBUS-SPOT-MULTISPECTRAL_NDWIw_moisture';
export const AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDVI = 'AIRBUS-SPOT-ARCHIVE-MULTISPECTRAL_NDVI';
export const AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI = 'AIRBUS-SPOT-ARCHIVE-MULTISPECTRAL_NDWIw';
export const AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE =
  'AIRBUS-SPOT-ARCHIVE-MULTISPECTRAL_NDWIw_moisture';

// Vantor/ Maxar/ Digital Globe
export const DIGITAL_GLOBE_50_HIGH_RES_TRUECOLOR = 'DIGITAL-GLOBE-50_high-res-truecolor';
export const DIGITAL_GLOBE_30_HIGH_RES_TRUECOLOR = 'DIGITAL-GLOBE-30_high-res-truecolor';

// Nearmap
export const NEARMAP_15_HIGH_RES_TRUECOLOR = 'NEARMAP-15_high-res-truecolor';
export const NEARMAP_50_HIGH_RES_TRUECOLOR = 'NEARMAP-50_high-res-truecolor';

// Planet Mosaics (formerly Planet Basemap)
export const PLANET_BASEMAP_HIGH_RES_TRUECOLOR = 'PLANET-BASEMAP_high-res-truecolor';
export const PLANET_SKYSAT_COLLECT_HIGH_RES_TRUECOLOR = 'PLANET-SKYSAT-COLLECT_high-res-truecolor';

// Chloris
export const CHLORIS_ABOVEGROUND_WOODY_BIOMASS = 'CHLORIS-BIOMASS_aboveground-woody-biomass';
export const CHLORIS_BIOMASS_STOCK = 'CHLORIS_CarbonStock';
export const CHLORIS_FOREST_COVER = 'CHLORIS_ForestCover';

export const MOBI_SPECIES_RICHNESS_ALL = 'MOBI_SpeciesRichness-All';
export const GLOBAL_FOREST_CHANGE_LOSSYEAR = 'GLOBAL-FOREST-CHANGE_lossyear';
export const GLOBAL_FOREST_COVER_FORESTCOVER = 'GLOBAL-FOREST-COVER_forestcover';
export const LAYER_PACKAGES = {
  [PLANET_FOREST_DILIGENCE_PACKAGE]: [
    PLANET_FOREST_ABOVEGROUND_CARBON,
    PLANET_FOREST_CANOPY_COVER,
    PLANET_FOREST_CANOPY_HEIGHT,
  ],
  [PLANET_FOREST_MONITORING_PACKAGE]: [
    PLANET_FOREST_ABOVEGROUND_CARBON_3M,
    PLANET_FOREST_CANOPY_COVER_3M,
    PLANET_FOREST_CANOPY_HEIGHT_3M,
  ],
  [CHLORIS_30_PACKAGE]: [
    CHLORIS_30_BIOMASS_STOCK,
    CHLORIS_30_BIOMASS_STOCK_CHANGE,
    CHLORIS_30_FOREST_COVER,
  ],
  [CHLORIS_10_PACKAGE]: [
    CHLORIS_10_BIOMASS_STOCK,
    CHLORIS_10_BIOMASS_STOCK_CHANGE,
    CHLORIS_10_FOREST_COVER,
  ],
};

// All layer keys that support bulk ordering. Includes multi-layer packages and
// single layers like CTrees that use per-feature scene selection instead of a bundle.
export const BULK_ORDER_LAYER_KEYS = new Set([...Object.keys(LAYER_PACKAGES), CTREES_TREE_HEIGHT]);

// Layer keys whose bulk orders use per-feature sensing time lookup rather than
// a single fixed sensing time.
export const PER_FEATURE_BULK_ORDER_SOURCE_IDS = new Set(['CTREES-TREE-HEIGHT']);

// Layers that don't have a preview image
export const NO_PREVIEW_LAYERS = [CTREES_TREE_HEIGHT];

export interface LayerTag {
  label: string;
  color: string;
}

export const FOREST_TAG = {label: 'Forest', color: '#C4DFC5'};
export const VEGETATION_TAG = {label: 'Vegetation', color: '#DDF3C7'};
export const FALSECOLOR_TAG = {label: 'False-color', color: '#FEDBD3'};
export const LANDCOVER_TAG = {label: 'Land cover', color: '#E3D4C7'};
export const FIRE_TAG = {label: 'Fire', color: '#FFBB8D'};
export const HYDROLOGY_TAG = {label: 'Hydrology', color: '#C1E1F3'};
export const TRUECOLOR_TAG = {label: 'Truecolor', color: '#FFF8D2'};
export const BIODIVERSITY_TAG = {label: 'Biodiversity', color: '#DCC7F2'};
export const INFRASTRUCTURE_TAG = {label: 'Infrastructure', color: 'lightGray'};
export const ENVIRONMENTAL_JUSTICE_TAG = {label: 'Environmental Justice', color: 'pink'};

export type DataLabels = readonly [string, string];
// Where "resolution" can refer to geospatial resolution (higher-resolution),
// temporal resolution (higher-frequency), etc.

export type ResolutionCategoryId = 'higher-resolution' | 'higher-frequency' | 'data';
// Where "index" refers to graphical indicator derived from satellite bands.

export type IndexCategoryId =
  | 'truecolor'
  | 'index'
  | 'falsecolor'
  | 'salo'
  | 'salo-carbon'
  | 'space-intelligence';
type MosaicType = 'monthly_start' | 'monthly_end' | 'annual' | ((date: Date) => [Date, Date]);

export type LayerLegendMap = Record<
  string,
  {color: string; label: string; order?: number; extendedDescription?: string}
>;

export interface RenderLayerLegendOptions {
  visibleLegendValueCoverage?: Record<string, number>;
  includeExtendedDescription?: boolean;
}

// Dedupes legend entries by label+color, since the same class can map to
// multiple pixel values.
function dedupeLegendEntries(
  entries: {color: string; label: string; order?: number; extendedDescription?: string}[]
): {color: string; label: string; order?: number; extendedDescription?: string}[] {
  const seen = new Set<string>();

  return entries.filter((entry) => {
    const key = `${entry.label}:::${entry.color}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

// Returns deduped legend entries, optionally filtered to only values visible
// on the map and sorted by their coverage area (most prevalent first).
export function getFilteredLayerLegendEntries(
  layerLegendMap: LayerLegendMap,
  options?: RenderLayerLegendOptions
) {
  const allEntries = dedupeLegendEntries(Object.values(layerLegendMap));
  const coverage = options?.visibleLegendValueCoverage;

  // No coverage data available — return the full legend.
  if (!coverage || !Object.keys(coverage).length) {
    return allEntries;
  }

  const filteredEntries = Object.entries(layerLegendMap)
    .filter(([value]) => value in coverage)
    .map(([value, entry]) => ({value, entry}));

  // No matching entries on the visible map — return the full legend rather
  // than showing an empty list (e.g. the map may still be loading tiles).
  if (!filteredEntries.length) {
    return allEntries;
  }

  filteredEntries.sort((a, b) => {
    const coverageDelta = (coverage[b.value] || 0) - (coverage[a.value] || 0);
    return coverageDelta !== 0 ? coverageDelta : a.entry.label.localeCompare(b.entry.label);
  });

  return dedupeLegendEntries(filteredEntries.map(({entry}, i) => ({...entry, order: i})));
}
/**
 * Layer that’s primarily just a visual overlay on the graph.
 */

export interface ImageLayerInfo {
  type: 'image';

  // Optional, as these are currently only used in Lens.
  resolutionCategory?: ResolutionCategoryId;
  indexCategory?: IndexCategoryId;

  key: string;

  // Layer name that may include source or resolution information.
  // Use this as the layer name when source info isn't displayed next
  // to it.
  display: string;
  // Layer name that does not include any source information.
  // Use this when displaying the layer name along side source information.
  shortName: string;

  renderLayerLegend?: (options?: RenderLayerLegendOptions) => React.ReactNode;
  layerLegendMap?: LayerLegendMap;

  // For layers whose raw tile encoding does not preserve the original COG class code,
  // this converts the byte PixelInfo recovers from the raw tile (255 - R) into the
  // key used by `layerLegendMap`. Used by GRASSLANDS_RISK, whose sparse uint16 codes
  // (5/7/100/500/1000/2000/5000) cannot fit in a single tile byte.
  pixelInfoDecode?: (recoveredByte: number) => number;

  thumbnailUrl?: string;
  highResPreview?: string;
  tags?: LayerTag[];
  description?: string | React.ReactNode;
  license?: {
    name: string;
    url: string | null;
    // Optional long-form license body. When provided, the library detail modal
    // renders a collapsible "Show full license terms" toggle.
    body?: string | React.ReactNode;
  };

  gradientStops?: readonly Readonly<[number, string]>[];

  // Called to conditionally filter out the layer from the U
  filter?: (featureCollection: ImmutableOf<ApiFeatureCollection>) => boolean;

  // Optionally override the source's resolution from CONSTANTS.SOURCE_DETAILS_BY_ID
  resolution?: number;

  // We'll display a static capture window for mosaics. E.g. the start & end date of
  // the month for a monthly mosaic.
  mosaic?: MosaicType;
  // We don't have accurate capture times for some sources (e.g. NAIP & Nearmap).
  onlyDisplayCaptureDate?: boolean;

  resampling?: 'linear' | 'nearest';

  // Allows for custom layer interactions in the library that aren't the usual add
  // or purchase
  libraryAction?: {text: string; link: string};
  allowedTiers?: LENS_TIERS_TYPE[];

  // User-facing string stating where the layer has available data
  geographicExtent?: string;
  // Polygon for where the layer has available data
  polygonExtent?: geojson.Polygon | geojson.MultiPolygon;

  // If true, the layer will be shown in the Analysis Tool, range overlay filter, and
  // other places that utilize raw layer data
  hasRawLayer?: boolean;

  // The vendor of the layer
  vendor: string;
}
/**
 * Layer that overlays on the map but its colors correspond to numeric values.
 *
 * Includes information necessary to render a graph of related data. Must
 * provide either a range and labels, or a unit to interpret the values with.
 */

export interface DataLayerInfo {
  type: 'data';

  // Optional, as these are currently only used in Lens.
  resolutionCategory?: ResolutionCategoryId;
  indexCategory?: IndexCategoryId;

  key: string;

  // Layer name that may include source or resolution information.
  // Use this as the layer name when source info isn't displayed next
  // to it.
  display: string;
  // Layer name that does not include any source information.
  // Use this when displaying the layer name along side source information.
  shortName: string;

  thumbnailUrl?: string;
  highResPreview?: string;
  tags?: LayerTag[];
  description?: string | React.ReactNode;

  // User-facing string stating where the layer has available data
  geographicExtent?: string;
  // Polygon for where the layer has available data
  polygonExtent?: geojson.Polygon;

  license?: {
    name: string;
    url: string | null;
    // Optional long-form license body. When provided, the library detail modal
    // renders a collapsible "Show full license terms" toggle.
    body?: string | React.ReactNode;
  };

  // The data range used in two ways:
  // A) The range for various displays like in charts
  // B) For unscaling pixel values in the underlying calculator used in some
  // parts of the app, like in Analyze Area where we convert values in a png
  // range [0, 255] to the layer's data range.
  dataRange: DataRange;
  // The data range for the raw tile layer, used by the pixel info tool when
  // decoding raw tiles that may be encoded differently from the display tiles.
  // This only needs to be set if the data range of the actual data is different
  // from the range we show in the app. IE for some sources like Airbus Multispectral,
  // we set a range for NDVI for app display that is different from NDVI_raw.
  rawDataRange?: DataRange;
  // The range a lookout condition may set its min/max over. Composite alert conditions
  // are evaluated against the layer's raw expression output (see backend
  // workers/lens_alerts/lens_alerts_utils.py), not the display dataRange, so a
  // normalized-difference index like NDVI/NDWI is reachable across its full [-1, 1]
  // math range even though dataRange is clipped for legible coloring. Kept separate
  // from rawDataRange, which decodes raw tiles for the pixel inspector and Analyze Area
  // and must not change just to widen what a rule can express.
  evaluableRange?: DataRange;
  dataLabels?: DataLabels;
  dataUnit?: string;
  // When true, pixel info values are rounded to the nearest integer. Useful for
  // layers where the source data is discrete integers but the 8-bit tile
  // encoding round-trip introduces fractions.
  hasRoundPixelInfoValues?: boolean;
  // This is used in planet carbon layers to treat all values above 100 as 100 when
  // rendering the image on the map. Use with caution!
  colorMapDataRange?: DataRange;

  cursorKey?: string;

  gradientStops: GradientStops;

  // All data layers can be viewed on the map, but for some like PRISM the resolution is
  // so low that it's not useful. Set this property to true to prevent the layer from being
  // displayed on the map and it will just be displayed in Analyze Area
  isNotDisplayedOnMap?: boolean;

  // Called to conditionally filter out the layer from the U
  filter?: (featureCollection: ImmutableOf<ApiFeatureCollection>) => boolean;

  // Optionally override the source's resolution from CONSTANTS.SOURCE_DETAILS_BY_ID
  resolution?: number;

  // We'll display a static capture window for mosaics. E.g. the start & end date of
  // the month for a monthly mosaic.
  mosaic?: MosaicType;
  // We don't have accurate capture times for some sources (e.g. NAIP & Nearmap).
  onlyDisplayCaptureDate?: boolean;

  resampling?: 'linear' | 'nearest';

  // Allows for custom layer interactions in the library that aren't the usual add
  // or purchase
  libraryAction?: {text: string; link: string};
  allowedTiers?: LENS_TIERS_TYPE[];
  vendor: string;

  // If true, the layer will be shown in the Analysis Tool, range overlay filter, and
  // other places that utilize raw layer data
  hasRawLayer?: boolean;
}

export type LayerInfo = ImageLayerInfo | DataLayerInfo;

export const ALL_LAYER_TAGS = [
  VEGETATION_TAG,
  LANDCOVER_TAG,
  TRUECOLOR_TAG,
  HYDROLOGY_TAG,
  FALSECOLOR_TAG,
  FOREST_TAG,
  FIRE_TAG,
  BIODIVERSITY_TAG,
  INFRASTRUCTURE_TAG,
  ENVIRONMENTAL_JUSTICE_TAG,
];

export const CUSTOM_GEOGRAPHIC_EXTENT = 'Custom';
export const GLOBAL_GEOGRAPHIC_EXTENT = 'Global';
export const CONTINENTAL_US_GEOGRAPHIC_EXTENT = 'Continental US';
export const TROPICS_GEOGRAPHIC_EXTENT = 'Tropics';
export const US_GEOGRAPHIC_EXTENT = 'US';
export const WESTERN_US_GEOGRAPHIC_EXTENT = 'Western US';
export const GRASSLANDS_RISK_GEOGRAPHIC_EXTENT = 'North America (southern Canada through Mexico)';

const GRASSLANDS_RISK_BBOX: GeoJSON.Polygon = {
  type: 'Polygon',
  coordinates: [
    [
      [-114.60604221779924, 20.81262670327998],
      [-90.14266599660904, 20.81262670327998],
      [-90.14266599660904, 54.129086933056804],
      [-114.60604221779924, 54.129086933056804],
      [-114.60604221779924, 20.81262670327998],
    ],
  ],
};

const WESTERN_US_BBOX: GeoJSON.Polygon = {
  type: 'Polygon',
  coordinates: [
    [
      [-125.0, 24.5],
      [-125.0, 49.5],
      [-100.0, 49.5],
      [-100.0, 24.5],
      [-125.0, 24.5],
    ],
  ],
};

// RCMAP is served well beyond the western US bbox, so gating on that one would hide the
// layers from features in the eastern part of the published extent.
const RCMAP_LAYER_FILTER = (featureCollection: ImmutableOf<ApiFeatureCollection>) =>
  _featureCollectionInBounds(featureCollection, RCMAP_WESTERN_NA_BBOX);

// For data only available in the contiguous US.
const CONUS_LAYER_FILTER = (featureCollection: ImmutableOf<ApiFeatureCollection>) =>
  _featureCollectionInUS(featureCollection);

/** Fetches the mosaic layer display behavior from our shared constants file.*/
function _getLayerMosaicDisplay(layerKey: string): MosaicType | undefined {
  const sourceId = layerKey.split('_', 1)[0];
  return SOURCE_DETAILS_BY_ID[sourceId]?.mosaic_display;
}

export const BUNDLE_SOURCE_MAPPINGS: Record<string, {truecolor: string; multispectral: string}> = {
  'AIRBUS-PNEO': {
    truecolor: 'AIRBUS-PNEO',
    multispectral: 'AIRBUS-PNEO-MULTISPECTRAL',
  },
  'AIRBUS-SPOT': {
    truecolor: 'AIRBUS-SPOT',
    multispectral: 'AIRBUS-SPOT-MULTISPECTRAL',
  },
  'AIRBUS-PLEIADES': {
    truecolor: 'AIRBUS-PLEIADES',
    multispectral: 'AIRBUS-PLEIADES-MULTISPECTRAL',
  },
  'AIRBUS-SPOT-ARCHIVE': {
    truecolor: 'AIRBUS-SPOT-ARCHIVE',
    multispectral: 'AIRBUS-SPOT-ARCHIVE-MULTISPECTRAL',
  },
  'AIRBUS-PNEO-ARCHIVE': {
    truecolor: 'AIRBUS-PNEO-ARCHIVE',
    multispectral: 'AIRBUS-PNEO-ARCHIVE-MULTISPECTRAL',
  },
  'AIRBUS-PLEIADES-ARCHIVE': {
    truecolor: 'AIRBUS-PLEIADES-ARCHIVE',
    multispectral: 'AIRBUS-PLEIADES-ARCHIVE-MULTISPECTRAL',
  },
};

export const bundleableSourceList = Object.values(BUNDLE_SOURCE_MAPPINGS).flatMap(Object.values);

// PUBLIC IDENTIFIER: the *keys* in this dict are used as URL slugs on
// upstream.tech (e.g. /lens/datasets/<layerKey>/). Do not rename a key
// without coordinating a redirect on the upstream.tech repo.
export const LAYERS: Record<string, LayerInfo> = {};

LAYERS[AIRBUS_PLEIADES_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_PLEIADES_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20Pleiades%20(0.5m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  tags: [TRUECOLOR_TAG],
  description:
    'Airbus Pléiades captures high-resolution satellite imagery worldwide at 0.5m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_ARCHIVE_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_PLEIADES_ARCHIVE_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20Pleiades%20(0.5m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  tags: [TRUECOLOR_TAG],
  description:
    'Airbus Pléiades captures high-resolution satellite imagery worldwide at 0.5m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Extended Archive does not receive the same level of verification and correction.',
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDVI,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus Pléiades Multispectral, Extended Archive)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_VEG.png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Extended Archive does not receive the same level of verification and correction.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI,
  dataRange: [-0.1, 0.4],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus Pléiades Multispectral, Extended Archive)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Extended Archive does not receive the same level of verification and correction.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE,
  dataRange: [-0.65, 0],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus Pléiades Multispectral, Extended Archive)',
  shortName: 'Surface Moisture',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_M.png',
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Extended Archive does not receive the same level of verification and correction.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_MULTISPECTRAL_NDVI,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus Pléiades Multispectral)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_VEG.png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
  hasRawLayer: true,
};

LAYERS[AIRBUS_PLEIADES_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_MULTISPECTRAL_NDWI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.4],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus Pléiades Multispectral)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PLEIADES_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PLEIADES_MULTISPECTRAL_NDWI_MOISTURE,
  hasRawLayer: true,
  dataRange: [-0.65, 0],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus Pléiades Multispectral)',
  shortName: 'Surface Moisture',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PLEIADES_M.png',
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus Pléiades, which captures high-resolution multispectral satellite imagery worldwide at 2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_SPOT_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20SPOT%20(1.5m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Airbus SPOT captures high-resolution satellite imagery worldwide at 1.5m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_ARCHIVE_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_SPOT_ARCHIVE_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20SPOT%20(1.5m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Airbus SPOT captures high-resolution satellite imagery worldwide at 1.5m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus SPOT Extended Archive does not receive the same level of verification and correction.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_MULTISPECTRAL_NDVI,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus SPOT)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_SPOT_VEG.png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
  hasRawLayer: true,
};

LAYERS[AIRBUS_SPOT_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_MULTISPECTRAL_NDWI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.4],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus SPOT)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_SPOT_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_MULTISPECTRAL_NDWI_MOISTURE,
  hasRawLayer: true,
  dataRange: [-0.65, 0],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus SPOT)',
  shortName: 'Surface Moisture',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_SPOT_M.png',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDVI,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus SPOT, Extended Archive)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(Airbus%20SPOT).png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus SPOT Extended Archive does not receive the same level of verification and correction.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI,
  dataRange: [-0.1, 0.4],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus SPOT, Extended Archive)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_SPOT_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus SPOT Extended Archive does not receive the same level of verification and correction.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_SPOT_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE,
  dataRange: [-0.65, 0],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus SPOT, Extended Archive)',
  shortName: 'Surface Moisture',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_SPOT_M.png',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus SPOT, which captures high-resolution multispectral satellite imagery worldwide at 6m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus SPOT Extended Archive does not receive the same level of verification and correction.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_PNEO_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20Neo%20(0.png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Airbus Pléiades Neo captures high-resolution satellite imagery worldwide at 0.3m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_ARCHIVE_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: AIRBUS_PNEO_ARCHIVE_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Airbus%20Neo%20(0.png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Airbus Pléiades Neo captures high-resolution satellite imagery worldwide at 0.3m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Neo Extended Archive does not receive the same level of verification and correction.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_MULTISPECTRAL_NDVI,
  hasRawLayer: true,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus Pléiades Neo)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_VEG.png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus Pléiades Neo, which captures high-resolution multispectral satellite imagery worldwide and provides it at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDVI,
  dataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (Airbus Pléiades Neo Extended Archive)',
  shortName: 'Vegetation',
  gradientStops: NDVI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_VEG.png',
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from -1 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from Airbus Pléiades Neo Extended Archive, which captures high-resolution multispectral satellite imagery worldwide at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors. The Airbus Pléiades Neo Extended Archive does not receive the same level of verification and correction.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_MULTISPECTRAL_NDWI,
  dataRange: [-0.1, 0.4],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus Pléiades Neo)',
  shortName: 'Surface Water',
  // TODO: add desired gradient stops
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus Pléiades Neo, which captures high-resolution multispectral satellite imagery worldwide at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_ARCHIVE_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_MULTISPECTRAL_NDWI_MOISTURE,
  dataRange: [-0.65, 0],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus Pléiades Neo)',
  shortName: 'Surface Moisture',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_M.png',
  // TODO: add desired gradient stops
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus Pléiades Neo, which captures high-resolution multispectral satellite imagery worldwide at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_MULTISPECTRAL_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_MULTISPECTRAL_NDWI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.4],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (Airbus Pléiades Neo)',
  shortName: 'Surface Water',
  // TODO: add desired gradient stops
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_SW.png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from Airbus Pléiades Neo, which captures high-resolution multispectral satellite imagery worldwide at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

LAYERS[AIRBUS_PNEO_MULTISPECTRAL_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: AIRBUS_PNEO_MULTISPECTRAL_NDWI_MOISTURE,
  hasRawLayer: true,
  dataRange: [-0.65, 0],
  rawDataRange: [-1, 1],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (Airbus Pléiades Neo)',
  shortName: 'Surface Moisture',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/MS_PNEO_M.png',
  // TODO: add desired gradient stops
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Moisture layer uses infrared and visible spectrum satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from Airbus Pléiades Neo, which captures high-resolution multispectral satellite imagery worldwide at 1.2m resolution. Imagery available can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Airbus End User License',
    url: 'https://www.intelligence-airbusds.com/legal/licences/',
  },
  vendor: 'Airbus',
};

// NOTE: Vantor used to be called Maxar, and was called Digital Globe before that.
// You'll see these names throughout the codebase, but they all refer to the same thing.
LAYERS[DIGITAL_GLOBE_50_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: DIGITAL_GLOBE_50_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Maxar%20Worldview%20(0.3m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Vantor WorldView 1, Worldview 2, and GeoEye-1 captures high-resolution satellite imagery worldwide at 0.5m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Vantor End User License',
    url: 'https://www.maxar.com/legal/internal-use-license',
  },
  vendor: 'Vantor',
};

LAYERS[DIGITAL_GLOBE_30_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: DIGITAL_GLOBE_30_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Maxar%20Worldview%20(0.3m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Vantor WorldView 3, Worldview 4, and Worldview Legion 1-6 captures high-resolution satellite imagery worldwide at 0.3m resolution. Imagery available in the archive can be ordered on a per-property basis in Lens and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Vantor End User License',
    url: 'https://www.maxar.com/legal/internal-use-license',
  },
  vendor: 'Vantor',
};

LAYERS[NEARMAP_15_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: NEARMAP_15_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Nearmap%20(0.15m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Nearmap captures high-resolution aerial imagery which we display at 0.15m resolution in Lens. Imagery available in the archive can be ordered on a per-property basis and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Nearmap End User License',
    url: 'https://www.nearmap.com/us/en/legal/products-agreement',
  },
  vendor: 'Nearmap',
};

LAYERS[NEARMAP_50_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: NEARMAP_50_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Nearmap%20(0.5m).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Nearmap captures high-resolution aerial imagery which we display at 0.5m resolution in Lens. Imagery available in the archive can be ordered on a per-property basis and costs scale by acres. Availability and timing of capture varies based on your geography and other factors.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Nearmap End User License',
    url: 'https://www.nearmap.com/us/en/legal/products-agreement',
  },
  vendor: 'Nearmap',
};

// NOTE: this layer is excepted from the isHighResTruecolor function
// because we generally want to bucket it separately.
LAYERS[PLANET_BASEMAP_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: PLANET_BASEMAP_HIGH_RES_TRUECOLOR,
  display: 'Truecolor (Planet Mosaics)',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Planet%20Basemap_lowres.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Planet%20Basemap.png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'PlanetScope visual basemaps are time series mosaics created from all scene captures within the month, optimized for consistency and minimal cloud cover. This is a recurring subscription that can be billed monthly or annually at $1,999 per month or $19,999 per year before tax. Non-profits are eligible for a 50% discount on this monthly or annual pricing using the code BASEMAPNGO50. Enrolled customers automatically receive new imagery for all properties in their account every month. PlanetScope mosaics are ideal for organizations with large holdings who are interested in high-frequency insights to identify landscape changes.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(PLANET_BASEMAP_HIGH_RES_TRUECOLOR),
  license: {
    name: 'Planet End User License',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[PLANET_SKYSAT_COLLECT_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: PLANET_SKYSAT_COLLECT_HIGH_RES_TRUECOLOR,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Planet%20Skysat.jpeg',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20Planet%20Skysat.jpeg',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'Planet SkySat is a constellation of 21 high-resolution satellites that capture imagery worldwide at 0.5m resolution. The SkySat archive can be ordered on a per-property basis and costs scale by the area ordered. The timing and availability of SkySat images varies widely and there is not a consistent pattern in past captures.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[NAIP_HIGH_RES_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: NAIP_HIGH_RES_TRUECOLOR,
  filter: CONUS_LAYER_FILTER,
  display: 'Truecolor',
  shortName: 'Truecolor',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20(NAIP).png',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  description:
    'USDA NAIP is high-resolution imagery captured for the continental United States roughly every three years dating back to the early 2000s. This 1-meter resolution imagery is captured during the summer months.',
  tags: [TRUECOLOR_TAG],
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  vendor: 'USDA',
};

LAYERS[NONE] = {
  type: 'image',
  resolutionCategory: 'data',
  key: NONE,
  display: 'None (Basemap)',
  shortName: 'Basemap',
  vendor: 'None',
};

LAYERS[S2_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: S2_NDVI,
  hasRawLayer: true,
  dataRange: [0, 1],
  evaluableRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (S2)',
  shortName: 'Vegetation',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(S2).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(S2)%20-%20HighRes.png',
  gradientStops: NDVI_GRADIENT_STOPS,
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from 0 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_EVI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: S2_EVI,
  hasRawLayer: true,
  dataRange: [0, 3],
  dataLabels: ['None', 'High Vigor'],
  display: 'Enhanced Vegetation (S2)',
  shortName: 'Enhanced Vegetation',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Enhanced%20Vegetation%20(S2).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Enhanced%20Vegetation%20(S2).png',
  gradientStops: NDVI_GRADIENT_STOPS,
  description: (
    <>
      Enhanced Vegetation Index (EVI) is a vegetation index that was developed to improve upon the
      Normalized Difference Vegetation Index (NDVI), particularly in areas with dense vegetation
      cover or in situations where atmospheric conditions may affect the quality of satellite
      imagery. EVI uses blue light to correct for the atmospheric influences and the canopy
      background signal, which can lead to more accurate assessments of vegetation health and
      density. Higher values indicate healthier and more dense vegetation. See{' '}
      <a
        href="https://www.sciencedirect.com/science/article/abs/pii/S0034425702000962"
        target="_blank"
        rel="noopener noreferrer"
      >
        Huete et al. (2002)
      </a>{' '}
      for details. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent
      captures.
    </>
  ),
  tags: [VEGETATION_TAG, FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NDCI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_NDCI,
  hasRawLayer: true,
  dataRange: [-0.2, 0.5],
  dataLabels: ['None', 'High'],
  display: 'Chlorophyll (S2)',
  shortName: 'Chlorophyll',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/NDCI%20(S2)%20-%20LowRes.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/NDCI%20(S2).png',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  description:
    'The Normalized Difference Chlorophyll Index (NDCI) is an index calculated using the red and red edge bands. This index is used to estimate the amount of chlorophyll in bodies of water and this layer is not meant to be used in areas of dry ground. Higher values indicate higher presence of chlorophyll. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NDTI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_NDTI,
  hasRawLayer: true,
  dataRange: [-1, 0.2],
  dataLabels: ['None', 'High'],
  display: 'Turbidity (S2)',
  shortName: 'Turbidity',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/NDTI%20(S2).png',
  gradientStops: GNBU_REVERSE_GRADIENT_STOPS,
  description:
    'The Normalized Difference Turbidity Index (NDTI) is useful for assessing water quality. By comparing the reflectance of specific wavelengths of light, typically in the visible and near-infrared spectrum, NDTI quantifies the turbidity level of water bodies. Turbidity is an indicator of suspended particles in water which may be related to pollution or sedimentation, which can impact aquatic species and ecosystems. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};
LAYERS[S2_AgNDTI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_AgNDTI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.6],
  dataLabels: ['Low residue', 'High residue'],
  display: 'Tillage (S2)',
  shortName: 'Tillage',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Tillage%20(S2).png',
  gradientStops: AG_NDTI_GRADIENT_STOPS,
  description: (
    <>
      <p>
        Normalized Difference Tillage Index (NDTI) is a remote sensing index derived from ESA
        Sentinel-2 that estimates soil disturbance and tillage intensity using short-wave infrared
        (SWIR) reflectance. NDTI is calculated as (SWIR2 - SWIR1) / (SWIR2 + SWIR1), leveraging the
        sensitivity of SWIR bands to exposed soil and crop residue cover.
      </p>
      <p>
        Values generally range from -0.1 to 0.6, with lower values indicating lower residue cover or
        more tillage, and higher values suggesting more residual dry matter or less tillage
        activity. Because NDTI can be influenced by soil moisture and crop type, it is best
        interpreted alongside true color imagery and vegetation indices. This dataset is available
        globally at 20 m spatial resolution and is visualized using a continuous earth-tone gradient
        to highlight relative differences in tillage intensity across fields.
      </p>
    </>
  ),
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
  tags: [VEGETATION_TAG],
};
LAYERS[S2_PHYCOCYANIN] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_PHYCOCYANIN,
  hasRawLayer: true,
  dataRange: [-500, 200],
  dataLabels: ['Low', 'High'],
  display: 'Phycocyanin (S2)',
  shortName: 'Phycocyanin',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Phycocyanin%20(S2).png',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  description: (
    <>
      <b>Please note that this is an experimental layer.</b> Phycocyanin is a natural pigment found
      in blue-green algae and some species of cyanobacteria. High concentrations of phycocyanin can
      be an indicator for algal bloom events which can impact people and aquatic ecosystems alike.
      This dataset utilizes a remote sensing formula tuned for phycocyanin pigment reflectance for
      inland water bodies.{' '}
      <a href="https://www.mdpcom/2072-4292/9/6/538" target="_blank" rel="noopener noreferrer">
        See this paper for more information.
      </a>
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_RRER] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_RRER,
  hasRawLayer: true,
  dataRange: [0, 5],
  dataLabels: ['Low', 'High'],
  display: 'Chlorophyll RRER (S2)',
  shortName: 'Chlorophyll RRER',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Chlorophyll%20RRER%20(S2).png',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  description: (
    <>
      <b>Please note that this is an experimental layer.</b> The Red-Red Edge Ratio (RRER) is a
      vegetation index used in remote sensing to assess plant health and chlorophyll content. It is
      calculated as the ratio of reflectance in Sentinel-2's red-edge band (B5) to the red band
      (B4). Higher values generally indicate greater chlorophyll concentration or healthy
      vegetation, while lower values can signal stress or sparse cover. Although designed for
      vegetation monitoring, this index can also be sensitive to certain algal pigments such as
      phycocyanin in water bodies.
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_BGRI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_BGRI,
  hasRawLayer: true,
  dataRange: [-1, 2],
  dataLabels: ['Low', 'High'],
  display: 'Blue - Green Ratio Index (S2)',
  shortName: 'BGRI',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Phycocyanin%20(S2).png',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  description: (
    <>
      <b>Please note that this is an experimental layer.</b> The Blue - Green Ratio Index (BGRI) is
      a vegetation index that is used to assess the health of vegetation. It is calculated by
      dividing the blue band by the green band.
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_TBI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_TBI,
  hasRawLayer: true,
  dataRange: [-1, 2],
  dataLabels: ['Low', 'High'],
  display: 'Three-Band Index (S2)',
  shortName: 'TBI',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Phycocyanin%20(S2).png',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  description: (
    <>
      <b>Please note that this is an experimental layer.</b> The Three-Band Index (TBI) is a
      vegetation index that is used to assess the health of vegetation. It is calculated by dividing
      the red edge band by the red edge band.
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[L8_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: L8_NDVI,
  hasRawLayer: true,
  dataRange: [0, 0.9],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (L8)',
  shortName: 'Vegetation',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(L8).png',
  gradientStops: NDVI_GRADIENT_STOPS,
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from 0 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.",
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[S2_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'truecolor',
  key: S2_TRUECOLOR,
  display: 'Truecolor (S2)',
  shortName: 'Truecolor',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20(S2).png',
  description:
    'ESA Sentinel-2 is a high frequency layer that goes back to late 2015 at 10m resolution. Data is captured almost every week, making it an ideal source for tracking changes over time. This truecolor layer may be lower resolution compared to commercial sources, but can be useful for detecting landscape change or pinpointing the timing of a change.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_FALSECOLOR_BLUE_SWIR] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'falsecolor',
  display: 'Falsecolor Blue, SWIR (S2)',
  shortName: 'Blue, SWIR',
  key: S2_FALSECOLOR_BLUE_SWIR,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Blue%20SWIR%20(S2)%20(2).png',
  description:
    'This layer shows blue light as red, and two different short-wave infrared (SWIR) bands as green and blue. This band combination is valuable in distinguishing snow, ice, and clouds. Ice on the ground will be bright red in this layer, while snow will appear orange, and clouds will range from white to dark peach. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[L8_FALSECOLOR_BLUE_SWIR] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'falsecolor',
  display: 'Falsecolor Blue, SWIR (L8)',
  shortName: 'Blue, SWIR',
  key: L8_FALSECOLOR_BLUE_SWIR,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Blue%20SWIR%20(L8).png',
  tags: [FALSECOLOR_TAG],
  description:
    'This layer shows blue light as red, and two different short-wave infrared (SWIR) bands as green and blue. This band combination is valuable in distinguishing snow, ice, and clouds. Ice on the ground will be bright red in this layer, while snow will appear orange, and clouds will range from white to dark peach. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.',
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[S2_FALSECOLOR_NIR_RED_GREEN] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'falsecolor',
  display: 'Falsecolor NIR, red, green (S2)',
  shortName: 'NIR, red, green',
  key: S2_FALSECOLOR_NIR_RED_GREEN,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/NIR%20red%2C%20green%20(S2).png',
  description:
    'This layer shows near-infrared (NIR) light as red, red light as green, and green light as blue. Plants reflect near-infrared and green light, while absorbing red, which makes this layer useful in assessing plant health. Since they reflect more near-infrared than green, plant-covered land appears deep red. Denser plant growth is darker red. Cities and exposed ground are gray or tan, and clear water is black. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[L8_FALSECOLOR_NIR_RED_GREEN] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'falsecolor',
  display: 'Falsecolor NIR, red, green (L8)',
  shortName: 'NIR, red, green',
  key: L8_FALSECOLOR_NIR_RED_GREEN,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/NIR%20red%2C%20green%20(L8).png',
  description:
    'This layer shows near-infrared (NIR) light as red, red light as green, and green light as blue. Plants reflect near-infrared and green light, while absorbing red, which makes this layer useful in assessing plant health. Since they reflect more near-infrared than green, plant-covered land appears deep red. Denser plant growth is darker red. Cities and exposed ground are gray or tan, and clear water is black. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[NAIP_FALSECOLOR_NIR_RED_GREEN] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'falsecolor',
  display: 'Falsecolor NIR, red, green (NAIP)',
  shortName: 'NIR, red, green',
  key: NAIP_FALSECOLOR_NIR_RED_GREEN,
  filter: CONUS_LAYER_FILTER,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/NIR%20red%2C%20green%20(NAIP).png',
  description:
    'This layer shows near-infrared (NIR) light as red, red light as green, and green light as blue. Plants reflect near-infrared and green light, while absorbing red, which makes this layer useful in assessing plant health. Since they reflect more near-infrared than green, plant-covered land appears deep red. Denser plant growth is darker red. Cities and exposed ground are gray or tan, and clear water is black. This layer is derived from USDA NAIP aerial data, which is captured in the continental United States on average every three years going back to the early 2000s.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  vendor: 'USDA',
};

LAYERS[S2_FALSECOLOR_SWIR_NIR_GREEN] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'falsecolor',
  display: 'Falsecolor SWIR, NIR, green (S2)',
  shortName: 'SWIR, NIR, green',
  key: S2_FALSECOLOR_SWIR_NIR_GREEN,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/SWIR%2C%20NIR%2C%20green%20(S2).png',
  description:
    'This layer shows near short-wave infrared (SWIR) light as red, NIR as green, and green light as blue. Because water and wet soil stand out in this band combination, it is valuable for monitoring floods. Saturated soil and sediment-laden water will appear blue. Ice clouds, snow, and ice are bright blue, and vegetated areas appear bright green. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[L8_FALSECOLOR_SWIR_NIR_GREEN] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'falsecolor',
  display: 'Falsecolor SWIR, NIR, green (L8)',
  shortName: 'SWIR, NIR, green',
  key: L8_FALSECOLOR_SWIR_NIR_GREEN,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/SWIR%2C%20NIR%2C%20green%20(L8).png',
  description:
    'This layer shows near short-wave infrared (SWIR) light as red, NIR as green, and green light as blue. Because water and wet soil stand out in this band combination, it is valuable for monitoring floods. Saturated soil and sediment-laden water will appear blue. Ice clouds, snow, and ice are bright blue, and vegetated areas appear bright green. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.',
  tags: [FALSECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[L8_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'truecolor',
  key: L8_TRUECOLOR,
  display: 'Truecolor (L8)',
  shortName: 'Truecolor',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20(L8).png',
  description:
    'NASA/USGS Landsat 8 is a high frequency layer that goes back to 2013 at 30m resolution. Data is captured approximately every other week, making it an ideal source for tracking changes over time. This truecolor layer may be lower resolution compared to commercial sources, but can be useful for detecting landscape change or pinpointing the timing of a change.',
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[L8_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: L8_NDWI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.4],
  dataLabels: ['Dry Land', 'Deep Water'],
  display: 'Surface Water (L8)',
  shortName: 'Surface Water',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Surface%20Water%20(L8).png',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[L8_NDSI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: L8_NDSI,
  display: 'Snow (L8)',
  shortName: 'Snow',
  dataRange: [0, 0.8],
  dataLabels: ['None', 'Snow'],
  gradientStops: [
    [0.0, '#FFFFFF'],
    [0.25, '#8888FF'],
    [0.75, '#0000FF'],
    [1.0, '#000088'],
  ],
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Snow%20(L8).png',
  description:
    'The Snow Index layer uses the Normalized Difference Snow Index (NDSI) to highlight areas on a property where snow is present. Lower values indicate a lack of snow and are shown in dark blue, while higher values denote areas with snow presence and are shown in white. This layer is derived from NASA/USGS Landsat 8 which has been capturing data since 2013.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[L8_NBR] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: L8_NBR,
  hasRawLayer: true,
  display: 'Burn Index (L8)',
  shortName: 'Burn Index',
  dataRange: [-0.1, 0.4],
  dataLabels: ['Low', 'High'],
  gradientStops: BURN_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Burn%20(L8).png',
  description:
    'Normalized burn ratio is an index calculated using near-infrared (NIR) and shortwave infrared (SWIR) values derived from NASA/USGS Landsat 8, launched in 2013. It highlights burnt areas in fire zones and denotes burn severity. Burnt areas display high SWIR and low NIR reflectance, which is the opposite of healthy vegetation. Values in Lens range from -0.1 to 0.4, where lower values denote healthy vegetation and higher values correspond to more intense burns. Because index values may vary based on the landscape, pre-fire vegetation, presence of smoke or clouds, it’s useful to view alongside truecolor, vegetation, and other datasets. NBR can provide insight into the timing and intensity of fires to assess post-fire conditions and monitor recovery.',
  tags: [FIRE_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[L8_NDBI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: L8_NDBI,
  hasRawLayer: true,
  display: 'Built-up Index (L8)',
  shortName: 'Built-up Index',
  dataRange: [0.1, 0.4],
  dataLabels: ['Low', 'High'],
  gradientStops: COPPER_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Built%20Up%20Index%20(L8)_lowres.png',
  description:
    'Normalized burn ratio is an index calculated using near-infrared (NIR) and shortwave infrared (SWIR) values derived from NASA/USGS Landsat 8, launched in 2013. It highlights burnt areas in fire zones and denotes burn severity. Burnt areas display high SWIR and low NIR reflectance, which is the opposite of healthy vegetation. Values in Lens range from -0.1 to 0.4, where lower values denote healthy vegetation and higher values correspond to more intense burns. Because index values may vary based on the landscape, pre-fire vegetation, presence of smoke or clouds, it’s useful to view alongside truecolor, vegetation, and other datasets. NBR can provide insight into the timing and intensity of fires to assess post-fire conditions and monitor recovery.',
  tags: [FIRE_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://www.usgs.gov/emergency-operations-portal/data-policy',
  },
  vendor: 'USGS',
};

LAYERS[HLS_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: HLS_NDVI,
  hasRawLayer: true,
  dataRange: [0.1, 1.0],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (HLS)',
  shortName: 'Vegetation',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(HLS).png',
  gradientStops: NDVI_GRADIENT_STOPS,
  description: (
    <>
      <div>
        The Harmonized Landsat and Sentinel-2 (HLS) Version 2.0 dataset is created with data from
        NASA/USGS's Landsat 8 and Landsat 9 along with ESA's Sentinel-2A, Sentinel-2B, and
        Sentinel-2C. Data from all of these satellites are gridded to the same resolution of 30m to
        create a harmonized data product with captures every 2-3 days. The HLS project is a
        collaboration between NASA, USGS, and ESA.
      </div>
      <div>
        Vegetation vigor is a measure of photosynthetic activity and is derived from the Normalized
        Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. Lower values
        indicate no vegetation present in white or yellow and high values indicate areas with
        vigorous vegetation in dark green.
      </div>
    </>
  ),
  tags: [VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Zero',
    url: 'https://creativecommons.org/public-domain/cc0/',
  },
  vendor: 'NASA/USGS & ESA',
};

LAYERS[HLS_TRUECOLOR] = {
  type: 'image',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'truecolor',
  key: HLS_TRUECOLOR,
  display: 'Truecolor (HLS)',
  shortName: 'Truecolor',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Truecolor%20(HLS).png',
  description: (
    <>
      <div>
        The Harmonized Landsat and Sentinel-2 (HLS) Version 2.0 dataset is created with data from
        NASA/USGS's Landsat 8 and Landsat 9 along with ESA's Sentinel-2A, Sentinel-2B, and
        Sentinel-2C. Data from all of these satellites are gridded to the same resolution of 30m to
        create a harmonized data product with captures every 2-3 days. The HLS project is a
        collaboration between NASA, USGS, and ESA.
      </div>
      <div>
        This truecolor layer may be lower resolution compared to commercial sources, but with
        frequent observations can be very useful in detecting landscape change or pinpointing timing
        of a change.
      </div>
    </>
  ),
  tags: [TRUECOLOR_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Zero',
    url: 'https://creativecommons.org/public-domain/cc0/',
  },
  vendor: 'NASA/USGS & ESA',
};

LAYERS[S1_WATER_CLASSIFICATION] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: S1_WATER_CLASSIFICATION,
  hasRawLayer: true,
  display: 'Surface Water (S1)',
  shortName: 'Surface Water',
  dataRange: [-0.1, 0],
  dataLabels: ['Dry', 'Water'],
  gradientStops: S1_WATER_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Surface%20Water%20(S1)_lowres.png',
  description:
    'This Surface Water dataset is derived from ESA Sentinel-1, launched in 2014 and captured approximately every 12 days. Sentinel-1 uses C-band synthetic aperture radar to collect data in any conditions, regardless of weather or time of day. Surface Water is displayed on a spectrum from dry to water, or white to blue respectively, and can be viewed visually or as a time-series in Analyze Area.  This dataset is ideal for assessing water conditions in areas with significant cloud cover or storms, and applications include monitoring changes in bodies of water or post-disaster assessments.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Public domain',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[ANY_TRUECOLOR_HIGH_RES] = {
  type: 'image',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'truecolor',
  key: ANY_TRUECOLOR_HIGH_RES,
  display: 'Truecolor (High Resolution)',
  shortName: 'Truecolor',
  gradientStops: DEFAULT_GRADIENT_STOPS,
  vendor: 'None',
};

LAYERS[S2_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: S2_NDWI,
  hasRawLayer: true,
  dataRange: [-0.1, 0.4],
  evaluableRange: [-1, 1],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (S2)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Surface%20Water%20(S2).png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: S2_NDWI_MOISTURE,
  hasRawLayer: true,
  dataRange: [-0.65, 0],
  evaluableRange: [-1, 1],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (S2)',
  shortName: 'Surface Moisture',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Surface%20Moisture%20(S2).png',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  description:
    'The Surface Moisture layer uses infrared and visible spectrum public satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NDSI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_NDSI,
  hasRawLayer: true,
  display: 'Snow (S2)',
  shortName: 'Snow',
  dataRange: [0, 0.8],
  dataLabels: ['None', 'Snow'],
  gradientStops: [
    [0.0, '#FFFFFF'],
    [0.25, '#8888FF'],
    [0.75, '#0000FF'],
    [1.0, '#000088'],
  ],
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Snow%20(S2).png',
  description:
    'The Snow Index layer uses the Normalized Difference Snow Index (NDSI) to highlight areas on a property where snow is present. Lower values indicate a lack of snow and are shown in dark blue, while higher values denote areas with snow presence and are shown in white. This layer is derived from ESA Sentinel-2 going back to late 2015 with frequent captures.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NBR] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_NBR,
  hasRawLayer: true,
  display: 'Burn Index (S2)',
  shortName: 'Burn Index',
  dataRange: [-0.1, 0.4],
  dataLabels: ['Low', 'High'],
  gradientStops: BURN_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Burn%20(S2).png',
  description:
    'Normalized burn ratio is an index calculated using near-infrared (NIR) and shortwave infrared (SWIR) values derived from ESA Sentinel-2, launched in 2015. It highlights burnt areas in fire zones and denotes burn severity. Burnt areas display high SWIR and low NIR reflectance, which is the opposite of healthy vegetation. Values in Lens range from -0.1 to 0.4, where lower values denote healthy vegetation and higher values correspond to more intense burns. Because index values may vary based on the landscape, pre-fire vegetation, presence of smoke or clouds, it’s useful to view alongside truecolor, vegetation, and other datasets. NBR can provide insight into the timing and intensity of fires to assess post-fire conditions and monitor recovery.',
  tags: [FIRE_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[S2_NDBI] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  resolution: 20,
  indexCategory: 'index',
  key: S2_NDBI,
  hasRawLayer: true,
  display: 'Built-up Index (S2)',
  shortName: 'Built-up Index',
  gradientStops: COPPER_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Built%20Up%20Index%20(S2).png',
  dataRange: [-1, 1],
  dataLabels: ['Low', 'High'],
  description:
    'Normalized difference built-up index is calculated using shortwave infrared (SWIR) and near-infrared (NIR) values derived from ESA Sentinel-2, launched in 2015. It highlights areas without vegetation, often capturing bare earth and impervious surfaces. Note that due to the usage of the SWIR band there may be some false positives around water and shorelines.',
  tags: [INFRASTRUCTURE_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Copernicus Sentinel Data Terms',
    url: 'https://sentinels.copernicus.eu/documents/247904/690755/Sentinel_Data_Legal_Notice',
  },
  vendor: 'ESA',
};

LAYERS[NLCD_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: NLCD_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (Legacy NLCD)',
  shortName: 'Landcover',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(NLCD).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(NLCD)%20-%20HighRes.png',
  layerLegendMap: NLCD_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(NLCD_LANDCOVER_VALUE_MAP, options)),
  // NLCD data is available in the contiguous US, Alaska, Hawaii, Puerto Rico.
  filter: (featureCollection) => _featureCollectionInUS(featureCollection),
  description:
    'Legacy NLCD data was developed by a partnership of multiple United States Federal agencies. Data are available for nine time points between 2001 and 2021 and covers the United States only. This dataset is no longer produced or updated and has been replaced by the Annual NLCD.',
  tags: [LANDCOVER_TAG],
  geographicExtent: US_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(NLCD_LANDCOVER),
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
};

LAYERS[NLCD_ANNUAL_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: NLCD_ANNUAL_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (Annual NLCD)',
  shortName: 'Landcover',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(Annual%20NLCD).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(Annual%20NLCD)%20-%20HighRes.png',
  layerLegendMap: NLCD_ANNUAL_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(NLCD_ANNUAL_LANDCOVER_VALUE_MAP, options)),
  // Annual NLCD data is available in the contiguous US.
  filter: (featureCollection) => _featureCollectionInUS(featureCollection),
  description:
    'The Annual National Land Cover Database (NLCD) is developed by USGS and provides annual land cover information for the United States back to 2000. Land is classified into one of 16 classes. This dataset is created from Landsat data using machine learning and AI and has replaced the Legacy NLCD.',
  tags: [LANDCOVER_TAG],
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(NLCD_ANNUAL_LANDCOVER),
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
};

LAYERS[NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE] = {
  type: 'data',
  resolutionCategory: 'data',
  key: NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE,
  hasRawLayer: true,
  display: 'Fractional Impervious Surface',
  shortName: 'Fractional Impervious Surface',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/NLCD-FIS.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: NLCD_ANNUAL_FIS_GRADIENT_STOPS,
  // Annual NLCD fractional impervious surface data is available in the contiguous US.
  filter: (featureCollection) =>
    _featureCollectionInUS(featureCollection, {exclude: ['AK', 'HI', 'PR']}),
  description: (
    <>
      The Annual NLCD Fractional Impervious Surface dataset is developed by USGS and provides the
      fractional surface area of each pixel that is covered with impervious surfaces such as
      pavement, concrete, or rooftops. This dataset is created from Landsat data using machine
      learning and AI and provides annual information for the United States back to 2000.{' '}
      <a
        href="https://www.usgs.gov/centers/eros/science/annual-nlcd-fractional-impervious-surface"
        target="_blank"
        rel="noopener noreferrer"
      >
        Click here for more information
      </a>
      .
    </>
  ),
  tags: [INFRASTRUCTURE_TAG],
  mosaic: _getLayerMosaicDisplay(NLCD_ANNUAL_FRACTIONAL_IMPERVIOUS_SURFACE),
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
};

LAYERS[EAG_PERCENT_COVER] = {
  type: 'data',
  resolutionCategory: 'data',
  key: EAG_PERCENT_COVER,
  hasRawLayer: true,
  display: 'Exotic Annual Grass Cover',
  shortName: 'Exotic Annual Grasses',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/exotic_annual_grasses.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: INVASIVE_GRASS_PERCENT_COVER_GRADIENT_STOPS,
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, WESTERN_US_BBOX),
  description: (
    <>
      The Exotic Annual Grass (EAG) dataset is developed by USGS and provides weekly percent cover
      estimates of exotic annual grasses — primarily cheatgrass (Bromus tectorum), medusahead
      (Taeniatherum caput-medusae), ventenata (Ventenata dubia), and Japanese brome (Bromus
      japonicus) — in the sagebrush biome of the western United States. This dataset is usually
      published weekly between April and June. Although this layer includes 16 grass species,
      cheatgrass was present in 85% of the field sites used to develop the model, making it a proxy
      of cheatgrass presence in the majority of coverage areas.{' '}
      <strong>
        USGS replaced this dataset with RCMAP-EAG in 2026, and it is no longer updated (last data:
        June 2025). Use Exotic Annual Grass (RCMAP) for current data.
      </strong>
    </>
  ),
  tags: [VEGETATION_TAG],
  mosaic: _getLayerMosaicDisplay(EAG_PERCENT_COVER),
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[CHEATGRASS_PERCENT_COVER] = {
  type: 'data',
  resolutionCategory: 'data',
  key: CHEATGRASS_PERCENT_COVER,
  hasRawLayer: true,
  display: 'Cheatgrass',
  shortName: 'Cheatgrass',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/cheatgrass.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: INVASIVE_GRASS_PERCENT_COVER_GRADIENT_STOPS,
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, WESTERN_US_BBOX),
  description: (
    <>
      The USGS Cheatgrass dataset provides annual fractional percent cover estimates of cheatgrass
      (Bromus tectorum) across the western United States sagebrush biome at 30m resolution.
      Estimates are derived from Harmonized Landsat and Sentinel-2 imagery and field observations.{' '}
      <strong>
        USGS replaced this dataset with RCMAP-EAG in 2026, and it is no longer updated (last data:
        2024). Use Cheatgrass (RCMAP) for current data.
      </strong>
    </>
  ),
  tags: [VEGETATION_TAG],
  mosaic: _getLayerMosaicDisplay(CHEATGRASS_PERCENT_COVER),
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_TOTAL_HERBACEOUS] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_TOTAL_HERBACEOUS,
  hasRawLayer: true,
  display: 'Total Herbaceous (RCMAP)',
  shortName: 'Total Herbaceous',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_total_herbaceous.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Grasses, forbs and cacti which were photosynthetically active at any point in the year of
      mapping. Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in the
      BLM AIM database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_GREEN_HERBACEOUS] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_GREEN_HERBACEOUS,
  hasRawLayer: true,
  display: 'Green Herbaceous (RCMAP)',
  shortName: 'Green Herbaceous',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_green_herbaceous.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Grasses, forbs and cacti which were photosynthetically active during the week of mapping.
      Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in the BLM AIM
      database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_SENESCED_HERBACEOUS] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_SENESCED_HERBACEOUS,
  hasRawLayer: true,
  display: 'Senesced Herbaceous (RCMAP)',
  shortName: 'Senesced Herbaceous',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/rcmap_senesced_herbaceous.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Grasses, forbs and cacti which were not photosynthetically active during the week of mapping.
      Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in the BLM AIM
      database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_TOTAL_EAG] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_TOTAL_EAG,
  hasRawLayer: true,
  display: 'Exotic Annual Grass (RCMAP)',
  shortName: 'Exotic Annual Grass',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_total_eag.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Combined cover of 13 exotic annual grass species, including field brome, cheatgrass,
      medusahead and ventenata. Modeled by USGS RCMAP at 30m from weekly HLS composites and field
      observations in the BLM AIM database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_CHEATGRASS] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_CHEATGRASS,
  hasRawLayer: true,
  display: 'Cheatgrass (RCMAP)',
  shortName: 'Cheatgrass',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_cheatgrass.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Cheatgrass (Bromus tectorum), one of the 13 species combined in the Exotic Annual Grass layer.
      Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in the BLM AIM
      database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_FIELD_BROME] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_FIELD_BROME,
  hasRawLayer: true,
  display: 'Field Brome (RCMAP)',
  shortName: 'Field Brome',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_fieldbrome.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Field brome (Bromus arvensis) and Japanese brome (Bromus japonicus), two of the 13 species
      combined in the Exotic Annual Grass layer. Modeled by USGS RCMAP at 30m from weekly HLS
      composites and field observations in the BLM AIM database and RCMAP plots, for early March
      through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_MEDUSAHEAD] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_MEDUSAHEAD,
  hasRawLayer: true,
  display: 'Medusahead (RCMAP)',
  shortName: 'Medusahead',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/rcmap_medusahead.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Medusahead (Taeniatherum caput-medusae), one of the 13 species combined in the Exotic Annual
      Grass layer. Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in
      the BLM AIM database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[RCMAP_SANDBERGS_BLUEGRASS] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  key: RCMAP_SANDBERGS_BLUEGRASS,
  hasRawLayer: true,
  display: "Sandberg's Bluegrass (RCMAP)",
  shortName: "Sandberg's Bluegrass",
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/rcmap_sandbergs_bluegrass.png',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  hasRoundPixelInfoValues: true,
  gradientStops: RCMAP_PERCENT_COVER_GRADIENT_STOPS,
  filter: RCMAP_LAYER_FILTER,
  description: (
    <>
      Sandberg's bluegrass (Poa secunda), mapped alongside the exotic annual grass components.
      Modeled by USGS RCMAP at 30m from weekly HLS composites and field observations in the BLM AIM
      database and RCMAP plots, for early March through early September.{' '}
      <a href={RCMAP_INFO_URL} target="_blank" rel="noopener noreferrer">
        See the USGS product page for methodology.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG],
  // A scene covers a whole published week, so keep the label in UTC instead of letting it
  // drift to the previous day for viewers west of UTC.
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.usa.gov/government-works',
  },
  resampling: 'nearest',
  vendor: 'USGS',
  geographicExtent: WESTERN_US_GEOGRAPHIC_EXTENT,
};

LAYERS[ESA_BIOMASS_STOCK] = {
  type: 'data',
  resolutionCategory: 'data',
  dataRange: [0, 500],
  dataLabels: ['0 MgC/ha', '500+ MgC/ha'],
  dataUnit: 'MgC',
  gradientStops: YLGN_GRADIENT_STOPS,
  key: ESA_BIOMASS_STOCK,
  hasRawLayer: true,
  display: 'ESA Biomass (100m)',
  shortName: 'Biomass',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/esa_biomass_v6_long.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/esa_biomass_v6_highres.png',
  description: (
    <>
      <div>
        ESA's Climate Change Initiative (CCI) Biomass project provides global data on above-ground
        biomass of woody vegetation for 2007, 2010, and annually from 2015-2022 at a 100m
        resolution. The global maps are generated from SAR data acquired by ESA's Sentinel-1A and B
        and JAXA's ALOS PALSAR and ALOS-2 PALSAR-2 satellites. The mapping builds on algorithms
        developed initially through ESA's GlobBiomass Project, with advancements from ESA CCI
        Biomass. Algorithms are informed by information on forest height and cover density from
        spaceborne LIDAR, including NASA's GEDI. For more information about the data, see{' '}
        <a href="https://esa-worldcover.org/cci-biomass" target="_blank" rel="noopener noreferrer">
          ESA's CCI Biomass project page
        </a>
        .
      </div>
      <br />
      <div>
        Santoro, M.; Cartus, O. (2025): ESA Biomass Climate Change Initiative (Biomass_cci): Global
        datasets of forest above-ground biomass for the years 2007, 2010, 2015, 2016, 2017, 2018,
        2019, 2020, 2021 and 2022, v6.0. NERC EDS Centre for Environmental Data Analysis, 17 April
        2025.{' '}
        <a
          href="https://doi.org/10.5285/95913ffb6467447ca72c4e9d8cf30501"
          target="_blank"
          rel="noopener noreferrer"
        >
          doi:10.5285/95913ffb6467447ca72c4e9d8cf30501
        </a>
        .
      </div>
    </>
  ),
  tags: [FOREST_TAG, VEGETATION_TAG, LANDCOVER_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'ESA CCI Biomass Terms and Conditions',
    url: 'https://artefacts.ceda.ac.uk/licences/specific_licences/esacci_biomass_terms_and_conditions.pdf',
  },
  mosaic: _getLayerMosaicDisplay(ESA_BIOMASS_STOCK),
  resampling: 'nearest',
  vendor: 'ESA',
};

LAYERS[ESA_WORLDCOVER_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: ESA_WORLDCOVER_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (ESA WorldCover)',
  shortName: 'Landcover',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(ESA%20WorldCover).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(ESA%20WorldCover)%20-%20HighRes.png',
  layerLegendMap: ESA_WORLDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(ESA_WORLDCOVER_VALUE_MAP, options)),
  description:
    'ESA WorldCover provides land cover classifications at 10m resolution globally for 2020 and 2021, and is based on data from ESA Sentinel-1 and Sentinel-2. This data layer was developed by a consortium of major European service providers and research organizations and includes 12 categories of land cover.',
  tags: [LANDCOVER_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Attribution Non Commercial Share Alike 4.0 International',
    url: 'https://spdx.org/licenses/CC-BY-NC-SA-4.0.html',
  },
  mosaic: _getLayerMosaicDisplay(ESA_WORLDCOVER_LANDCOVER),
  resampling: 'nearest',
  vendor: 'ESA',
};

LAYERS[IMPACT_OBSERVATORY_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: IMPACT_OBSERVATORY_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (Impact Observatory)',
  shortName: 'Landcover',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(Impact%20Observatory).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/Landcover%20(Impact%20Observatory)%20-%20High%20Res.png',
  layerLegendMap: IMPACT_OBSERVATORY_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(IMPACT_OBSERVATORY_LANDCOVER_VALUE_MAP, options)),
  description:
    'This dataset, produced by Impact Observatory, Microsoft, and Esri, displays a global map of land use/land cover (LULC) derived from ESA Sentinel-2 imagery at 10m resolution back to 2017. Each year is generated from Impact Observatory’s deep learning AI land classification model using a massive training dataset of billions of human-labeled image pixels. The global maps were produced by applying this model to every Sentinel-2 scene, processing over 400,000 Earth observations per year. The algorithm generates LULC predictions for 9 classes globally. These classifications include Built, Crops, Trees, Water, Rangeland, Flooded Vegetation, Snow/Ice, Bare Ground, and Clouds.',
  tags: [LANDCOVER_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  mosaic: _getLayerMosaicDisplay(IMPACT_OBSERVATORY_LANDCOVER),
  resampling: 'nearest',
  vendor: 'Impact Observatory',
};

LAYERS[IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER,
  hasRawLayer: true,
  display: 'Custom Landcover (Impact Observatory)',
  shortName: 'Custom Landcover',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/High%20Res%20Landcover%20(Impact%20Observatory).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/High%20Res%20Landcover%20(Impact%20Observatory)%20-%20HiRes.png',
  onlyDisplayCaptureDate: true,
  layerLegendMap: IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(IMPACT_OBSERVATORY_HIGH_RES_LANDCOVER_VALUE_MAP, options)
    ),
  description: (
    <>
      <div>
        This dataset, produced by Impact Observatory, displays land use/land cover (LULC) derived
        from ESA Sentinel-2 imagery at 10m resolution for a custom area and time period. Impact
        Observatory's innovative AI-powered methods categorize land cover into 14 classes: Water
        Channel Extent, Variable Water, Persistent Water, Dense Trees, Sparse Trees, Dense
        Rangeland, Sparse Rangeland, Flooded Vegetation, Crops, High Density Built, Low Density
        Built, Bare Ground, Snow/Ice, Clouds.
      </div>
      <br />
      <div>
        The cost to request this custom land cover dataset is 1 cent per acre, with a minimum spend
        of $500, and data delivery will include the time period requested and one year prior for
        comparison. Click the “Request” button to get a quote for your area.
      </div>
    </>
  ),
  tags: [LANDCOVER_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Impact Observatory End User License Agreement',
    url: 'https://www.impactobservatory.com/legal/eula/',
  },
  resampling: 'nearest',
  libraryAction: {
    text: 'Request',
    link: 'https://form.asana.com/?k=enNwaZ1O-_S01_P5R5caYw&d=253187955686276',
  },
  vendor: 'Impact Observatory',
};

LAYERS[MAPBIOMAS_BRAZIL_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: MAPBIOMAS_BRAZIL_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (MapBiomas Brasil)',
  shortName: 'Landcover (Brazil)',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Mapbiomas%20Brazil%20Landcover.png',
  layerLegendMap: MAPBIOMAS_BRAZIL_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(MAPBIOMAS_BRAZIL_LANDCOVER_VALUE_MAP, options)),
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, BRAZIL_SHAPE),
  description: (
    <>
      <div>
        The MapBiomas Brasil dataset provides annual land use and land cover maps for Brazil from
        2000 to the latest available year, created through a collaboration between NGOs,
        universities and technology companies. This dataset includes dozens of land cover categories
        and offers a comprehensive view of how Brazil's landscapes have transformed over time,
        making it an essential resource for monitoring environmental changes. The project is
        continually updated to improve accuracy and coverage. Detailed accuracy information and
        methodological insights are available{' '}
        <a href="https://brasil.mapbiomas.org/en/" target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </div>
    </>
  ),
  tags: [BIODIVERSITY_TAG, LANDCOVER_TAG],
  geographicExtent: 'Brazil',
  polygonExtent: BRAZIL_SHAPE,
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(MAPBIOMAS_BRAZIL_LANDCOVER),
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  resampling: 'nearest',
  vendor: 'MapBiomas',
};

LAYERS[WLFW_CO_ISI] = {
  type: 'image',
  resolutionCategory: 'data',
  key: WLFW_CO_ISI,
  hasRawLayer: true,
  display: 'Invasion Severity Index (CO)',
  shortName: 'Invasion Severity Index',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/wlfw_co_isi.png',
  layerLegendMap: WLFW_CO_ISI_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(WLFW_CO_ISI_VALUE_MAP, options)),
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, COLORADO_SHAPE),
  description: (
    <>
      <div>
        This layer provides information on invasive annual grasses and the Invasion Severity Index
        (ISI). The ISI is a strategic planning tool designed to prioritize invasive annual grass
        management across the sagebrush biome. Using 10-meter resolution data from the Rangeland
        Analysis Platform, the ISI assesses the severity of annual grass invasion relative to
        perennial vegetation and bare ground. This layer categorizes the landscape into{' '}
        <a
          href="https://storage.googleapis.com/wlfw-public/isi_materials/ISI_severity_levels.jpg"
          target="_blank"
          rel="noopener noreferrer"
        >
          five levels
        </a>{' '}
        ranging from intact cores to high-severity states with recommended management activities
        from maintaining to revegetating.
      </div>
      <br />
      <div>
        Tilley, D., Maestas, J., Smith, J., Mueller, K., Morford, S. and D. Naugle. 2026. Invasion
        Severity Index: Maps and Web App to Support Strategic Invasive Annual Grass Management.
        Working Lands for Wildlife.{' '}
        <a
          href="https://www.wlfw.org/invasion-severity-index/"
          target="_blank"
          rel="noopener noreferrer"
        >
          https://www.wlfw.org/invasion-severity-index/
        </a>
      </div>
    </>
  ),
  tags: [VEGETATION_TAG],
  geographicExtent: 'Colorado',
  polygonExtent: COLORADO_SHAPE,
  resolution: 10,
  mosaic: _getLayerMosaicDisplay(WLFW_CO_ISI),
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  resampling: 'nearest',
  vendor: 'University of Montana / NRCS',
};

LAYERS[MAPBIOMAS_CHACO_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: MAPBIOMAS_CHACO_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (MapBiomas Chaco)',
  shortName: 'Landcover (Chaco)',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Mapbiomas%20Chaco%20Landcover.png',
  layerLegendMap: MAPBIOMAS_CHACO_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(MAPBIOMAS_CHACO_LANDCOVER_VALUE_MAP, options)),
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, CHACO_BBOX),
  description: (
    <>
      <div>
        The MapBiomas Chaco dataset offers annual land cover and land use maps for the Gran Chaco
        Americano, covering Argentina, Bolivia, Brazil, and Paraguay from 2000 to 2022. This
        initiative is a collaborative effort involving local experts in land use, satellite remote
        sensing, and GIS to understand the dynamic changes in this unique region. Using advanced
        cloud processing and automated classifiers on the Google Earth Engine platform, the project
        provides an open, accessible, and updated resource for monitoring environmental
        transformations in the Gran Chaco. More information about this dataset can be found{' '}
        <a href="https://chaco.mapbiomas.org/en/" target="_blank" rel="noopener noreferrer">
          here
        </a>
        .
      </div>
    </>
  ),
  tags: [BIODIVERSITY_TAG, LANDCOVER_TAG],
  geographicExtent: 'Argentina, Bolivia, Brazil, and Paraguay',
  polygonExtent: CHACO_BBOX,
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(MAPBIOMAS_CHACO_LANDCOVER),
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  resampling: 'nearest',
  vendor: 'MapBiomas',
};

LAYERS[MAPBIOMAS_COLOMBIA_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: MAPBIOMAS_COLOMBIA_LANDCOVER,
  hasRawLayer: true,
  display: 'Landcover (MapBiomas Colombia)',
  shortName: 'Landcover (Colombia)',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Mapbiomas%20Colombia%20Landcover.png',
  layerLegendMap: MAPBIOMAS_COLOMBIA_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(MAPBIOMAS_COLOMBIA_LANDCOVER_VALUE_MAP, options)),
  filter: (featureCollection) => _featureCollectionInBounds(featureCollection, COLOMBIA_SHAPE),
  description: (
    <>
      <div>
        The MapBiomas Colombia dataset provides annual land use and land cover maps for Colombia
        from 2000 to the latest available year. This data is created through the collaboration of
        experts in earth sciences, remote sensing, GIS, and programming. Using advanced cloud
        processing and automated classifiers on the Google Earth Engine platform, the project
        provides an open, accessible, and updated resource for monitoring land cover and land use
        changes in Colombia. More information can be found at{' '}
        <a href="https://colombia.mapbiomas.org/en/" target="_blank" rel="noopener noreferrer">
          Mapbiomas Colombia's site
        </a>
        .
      </div>
    </>
  ),
  tags: [BIODIVERSITY_TAG, LANDCOVER_TAG],
  geographicExtent: 'Colombia',
  polygonExtent: COLOMBIA_SHAPE,
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(MAPBIOMAS_COLOMBIA_LANDCOVER),
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  resampling: 'nearest',
  vendor: 'MapBiomas',
};

LAYERS[SBTN_NATURAL_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: SBTN_NATURAL_LANDCOVER,
  hasRawLayer: true,
  display: 'Natural Lands Classification (WRI)',
  shortName: 'Natural Lands Classification (WRI)',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/WRI%20Landcover%20Classification.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/sbtn-highres.png',
  layerLegendMap: SBTN_NATURAL_LANDS_CLASSIFICATION_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(SBTN_NATURAL_LANDS_CLASSIFICATION_VALUE_MAP, options)
    ),
  description: (
    <>
      <div>
        The WRI Natural Lands Classification layer, part of the Science Based Targets Network
        (SBTN), provides a comprehensive global map of natural lands as of 2020, designed to support
        companies in setting "no conversion of natural ecosystems" targets. Developed by WRI's Land
        & Carbon Lab in collaboration with the World Wildlife Fund and Systemiq, this dataset
        combines the best available global and local geospatial data to establish a common baseline
        for visualizing natural land extent. The map helps companies assess their responsibility for
        land conversion post-2020 and make informed decisions about their operations and supply
        chains. See technical documentation
        <a
          href="https://sciencebasedtargetsnetwork.org/wp-content/uploads/2024/09/Technical-Guidance-2024-Step3-Land-v1-Natural-Lands-Map.pdf"
          target="_blank"
          rel="noopener noreferrer"
        >
          {' here '}
        </a>
        for more detail.
      </div>
    </>
  ),
  tags: [BIODIVERSITY_TAG, LANDCOVER_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(SBTN_NATURAL_LANDCOVER),
  license: {
    name: 'Creative Commons Noncommercial Sharealike 3.0 Intergovernmental Organization License',
    url: 'https://creativecommons.org/licenses/by-nc-sa/4.0/deed.en',
  },
  resampling: 'nearest',
  vendor: 'WRI',
};

LAYERS[CDL_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: CDL_LANDCOVER,
  hasRawLayer: true,
  display: 'Cropland Data Layer (USDA)',
  shortName: 'Cropland Data Layer',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/cdl.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/cdl.png',
  layerLegendMap: CDL_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(CDL_LANDCOVER_VALUE_MAP, options)),
  // CDL data is available in the contiguous US.
  filter: (featureCollection) => _featureCollectionInUS(featureCollection),
  description:
    'The Cropland Data Layer (CDL) is produced by the USDA National Agricultural Statistics Service (NASS) using both satellite imagery and ground data. This data layer provides crop-specific land cover classification for the United States annually.',
  tags: [LANDCOVER_TAG, VEGETATION_TAG],
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CDL_LANDCOVER),
  license: {
    name: 'Creative Commons CCZero',
    url: 'https://opendefinition.org/licenses/cc-zero/',
  },
  resampling: 'nearest',
  vendor: 'USDA',
};

LAYERS[WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER,
  hasRawLayer: true,
  display: 'Global Grassland Extent',
  shortName: 'Global Grassland Extent',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/global_grassland_extent.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/global_grassland_extent_highres.png',
  layerLegendMap: WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER_VALUE_MAP, options)
    ),
  description: (
    <>
      <div>
        Created by the Global Pasture Watch (GPW) research consortium initiated by the Land & Carbon
        Lab, the global grassland extent maps provide global information delineating cultivated
        grasslands and natural/semi-natural grasslands at a 30-meter resolution. This dataset is
        created using a machine learning framework that uses Landsat data, Earth Observation
        predictor variables, and over 2.3 million reference samples. This data is valuable for
        tracking changes in grassland ecosystems, informing land-use management, and guiding
        conservation efforts. For more information, see{' '}
        <a
          href="https://landcarbonlab.org/data/global-grassland-and-livestock-monitoring/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Land & Carbon Lab's website
        </a>
        .{' '}
        <a href="https://openlandmap.org/" target="_blank" rel="noopener noreferrer">
          OpenLandMap.org
        </a>{' '}
        hosts this data and had made it accessible for us to pull into Lens.
      </div>
      <br />
      <div>
        Parente, L., Sloat, L., Mesquita, V., et al. (2024). Annual 30-m maps of global grassland
        class and extent (2000-2022) based on spatiotemporal Machine Learning, Scientific Data.{' '}
        <a
          href="http://doi.org/10.1038/s41597-024-04139-6"
          target="_blank"
          rel="noopener noreferrer"
        >
          http://doi.org/10.1038/s41597-024-04139-6
        </a>
      </div>
    </>
  ),
  tags: [LANDCOVER_TAG, VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(WRI_GLOBAL_GRASSLAND_PASTURE_LANDCOVER),
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  resampling: 'nearest',
  vendor: 'Land & Carbon Lab (via OpenLandMap)',
};

LAYERS[GRASSLANDS_RISK] = {
  type: 'image',
  resolutionCategory: 'data',
  key: GRASSLANDS_RISK,
  hasRawLayer: true,
  display: 'Grasslands Risk (30m)',
  shortName: 'Grasslands Risk',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/grasslands_risk_v2.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/grasslands_risk_v2.png',
  layerLegendMap: GRASSLANDS_RISK_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(GRASSLANDS_RISK_VALUE_MAP, options)),
  // Inverse of backend GRASSLANDS_RISK_RAW_BYTE_BY_CODE in common/colormaps.py.
  pixelInfoDecode: (recoveredByte) =>
    ({1: 5, 2: 7, 3: 100, 4: 500, 5: 1000, 6: 2000, 7: 5000})[recoveredByte] ?? recoveredByte,
  filter: (featureCollection) =>
    _featureCollectionInBounds(featureCollection, GRASSLANDS_RISK_BBOX),
  description: (
    <>
      The Grasslands Risk Map (v2) is provided by the Central Grasslands Roadmap Initiative and the
      U.S. Fish & Wildlife Service. It provides a biome-level assessment of the primary drivers of
      grassland loss — cropland conversion and woody encroachment — categorizing the landscape into
      core intact grasslands, areas under immediate threat, and areas requiring strategic
      restoration or reconnection. This layer helps prioritize conservation actions where they will
      be most effective.
    </>
  ),
  tags: [VEGETATION_TAG],
  geographicExtent: GRASSLANDS_RISK_GEOGRAPHIC_EXTENT,
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(GRASSLANDS_RISK),
  license: {
    name: 'Custom (USFWS)',
    url: 'https://drive.google.com/file/d/1HL0-9mOB4w9Mz35ukZRuaiYqoCdHqVHY/view',
    body: 'United States Fish and Wildlife Service (Service) shall not be held liable for improper or incorrect use of the data described and/or contained herein. While the Service makes every reasonable effort to ensure the accuracy and completeness of data provided for distribution, it may not have the necessary accuracy or completeness required for every possible intended use. The Service recommends that data users consult the associated metadata record to understand the quality and possible limitations of the data. The Service creates metadata records in accordance with the standards endorsed by the Federal Geographic Data Committee. As a result of the above considerations, the Service gives no warranty, expressed or implied, as to the accuracy, reliability, or completeness of the data. It is the responsibility of the data user to use the data in a manner consistent with the limitations of geospatial data in general and these data in particular. Although these data have been processed successfully on a computer system at the Service, no warranty, expressed or implied, is made regarding the utility of the data on another system or for general or scientific purposes, nor shall the act of distribution constitute any such warranty. This applies to the use of the data both alone and in aggregate with other data and information.',
  },
  resampling: 'nearest',
  vendor: 'U.S. Fish & Wildlife Service',
};

LAYERS[METAWRI_CANOPY_HEIGHT] = {
  type: 'data',
  resolutionCategory: 'data',
  indexCategory: 'index',
  key: METAWRI_CANOPY_HEIGHT,
  hasRawLayer: true,
  display: 'Canopy Height (Meta WRI)',
  dataRange: [0, 35],
  dataLabels: ['0', '35 Meters'],
  dataUnit: 'm',
  gradientStops: GREENS_GRADIENT_STOPS,
  shortName: 'Canopy Height',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/meta-canopy-height-thumbnail.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/meta-canopy-height-highres.png',
  description: (
    <>
      <div>
        The Global Canopy Height dataset provides 1m resolution maps of canopy height, developed
        using self-supervised learning models trained on aerial imagery and GEDI Lidar. This dataset
        was created using sub-meter satellite imagery from Vantor between 2009 through 2020, making
        it a helpful supplement to baselines. This high-resolution data was developed to drive
        transparency and accountability in carbon markets and conservation efforts. More information
        can be found in
        <a
          href="https://www.sciencedirect.com/science/article/pii/S003442572300439X"
          target="_blank"
          rel="noopener noreferrer"
        >
          {' this research paper. '}
        </a>
      </div>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  resolution: 1,
  mosaic: _getLayerMosaicDisplay(METAWRI_CANOPY_HEIGHT),
  license: {
    name: 'Creative Commons 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/deed.en',
  },
  vendor: 'Meta/WRI',
};

LAYERS[CTREES_TREE_HEIGHT] = {
  type: 'data',
  resolutionCategory: 'data',
  indexCategory: 'index',
  key: CTREES_TREE_HEIGHT,
  hasRawLayer: true,
  display: 'Tree Canopy Height (CTrees)',
  shortName: 'Tree Canopy Height',
  dataRange: [0, 30],
  dataLabels: ['0', '30+ Meters'],
  dataUnit: 'm',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/ctrees_thumbnail.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/ctrees.png',
  // CTrees is US-only.
  filter: (featureCollection) => _featureCollectionInUS(featureCollection),
  description: (
    <>
      <div>
        <a href="https://ctrees.org/" target="_blank" rel="noopener noreferrer">
          CTrees.org
        </a>{' '}
        Tree Canopy Height provides 60cm spatial resolution tree canopy height for the contiguous
        United States. This data can be purchased in the order pane for individual properties at
        $0.10/acre.
      </div>
      <br />
      <div>
        Heights are estimated from NAIP quarter-quad mosaics. Standard tree canopy height may vary
        in urban areas. Please reach out if you are interested in urban-specific products.{' '}
        <a
          href="https://doi.org/10.1016/j.rse.2024.114099"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about the methodology here
        </a>
        .
      </div>
    </>
  ),
  license: {
    name: 'CTrees EULA',
    url: 'https://drive.google.com/file/d/1TCAw720DaTvFxCUJsjQtV7AN6k-wlV35/view',
  },
  tags: [FOREST_TAG],
  geographicExtent: US_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CTREES_TREE_HEIGHT),
  vendor: 'CTrees',
};

LAYERS[TMF_ANNUAL_CHANGE_LANDCOVER] = {
  type: 'image',
  resolutionCategory: 'data',
  key: TMF_ANNUAL_CHANGE_LANDCOVER,
  hasRawLayer: true,
  display: 'Tropical Moist Forest Landcover (EC)',
  shortName: 'Tropical Moist Forest Landcover',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/TMF%20Annual%20Change.png',
  layerLegendMap: TMF_ANNUAL_CHANGE_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(TMF_ANNUAL_CHANGE_VALUE_MAP, options)),
  description: (
    <>
      <div>
        The annual change collection dataset, developed by the European Commission's Joint Research
        Centre (JRC), provides annual classifications of tropical moist forest (TMF) land cover from
        2000 to 2023, available in tropical forest regions only. It classifies the forest into six
        categories: intact/undisturbed, degraded, deforested, regrowing, water, and other. The
        dataset is a valuable tool for tracking changes in tropical forest conditions and monitoring
        changes such as deforestation, forest degradation, and regrowth over time.{' '}
        <a
          href="https://forobs.jrc.ec.europa.eu/TMF/data"
          target="_blank"
          rel="noopener noreferrer"
        >
          See here for more information
        </a>
        .
      </div>
    </>
  ),
  tags: [BIODIVERSITY_TAG, FOREST_TAG, LANDCOVER_TAG],
  geographicExtent: 'Tropical forest regions',
  license: {name: 'None (Public Use)', url: ''},
  resolution: 30,
  mosaic: _getLayerMosaicDisplay(TMF_ANNUAL_CHANGE_LANDCOVER),
  resampling: 'nearest',
  vendor: 'European Commission Joint Research Centre',
};

LAYERS[S2_NDWI_M] = {
  type: 'data',
  key: S2_NDWI_M,
  hasRawLayer: true,
  display: 'Leaf Moisture (S2)',
  shortName: 'Leaf Moisture',
  dataRange: [-0.2, 0.45],
  dataLabels: ['Lower', 'Higher'],
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  vendor: 'ESA',
};

LAYERS[NAIP_NDVI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: NAIP_NDVI,
  dataRange: [-0.1, 0.5],
  rawDataRange: [-1, 1],
  dataLabels: ['None', 'High Vigor'],
  display: 'Vegetation (NAIP)',
  shortName: 'Vegetation',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Vegetation%20(NAIP).png',
  gradientStops: NDVI_GRADIENT_STOPS,
  filter: CONUS_LAYER_FILTER,
  description:
    "Vegetation vigor is a measure of photosynthetic activity, or how much plant growth is occurring. It's derived from the Normalized Difference Vegetation Index (NDVI) using visible and near-infrared reflectance. The vegetation layer ranges from 0 to 1, with low values indicating no vegetation present in white or yellow and high values showing areas with vigorous vegetation in dark green. This layer is derived from USDA NAIP aerial data, which is captured in the continental United States on average every three years going back to the early 2000s.",
  tags: [VEGETATION_TAG],
  geographicExtent: US_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  vendor: 'USDA',
  hasRawLayer: true,
};

LAYERS[NAIP_NDWI] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: NAIP_NDWI,
  dataRange: [0, 0.55],
  dataLabels: ['Dry', 'Water'],
  display: 'Surface Water (NAIP)',
  shortName: 'Surface Water',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  filter: CONUS_LAYER_FILTER,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Surface%20Water%20(NAIP).png',
  description:
    'The Surface Water layer is derived from the Normalized Difference Water Index (NDWI) and shows which parts of a property have standing water present. Values range from -0.1 to 0.4, where low values show dry land and higher values in blue show areas with water, such as rivers or ponds. This layer is derived from USDA NAIP aerial data, which is captured in the continental United States on average every three years going back to the early 2000s.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  vendor: 'USDA',
};

LAYERS[NAIP_NDWI_MOISTURE] = {
  type: 'data',
  resolutionCategory: 'higher-resolution',
  indexCategory: 'index',
  key: NAIP_NDWI_MOISTURE,
  dataRange: [-0.25, 0.3],
  dataLabels: ['Dry', 'Saturated'],
  display: 'Surface Moisture (NAIP)',
  shortName: 'Surface Moisture',
  gradientStops: S2_NDWI_GRADIENT_STOPS,
  filter: CONUS_LAYER_FILTER,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Surface%20Moisture%20(NAIP).png',
  description:
    'The Surface Moisture layer uses infrared and visible spectrum public satellite data to identify moist bare soil and very shallow water. Derived from the Normalized Difference Water Index (NDWI), this layer is best used in areas with bare ground or minimal vegetation to understand saturated ground, water bodies, and streams. This layer is derived from USDA NAIP aerial data, which is captured in the continental United States on average every three years going back to the early 2000s.',
  tags: [HYDROLOGY_TAG],
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  onlyDisplayCaptureDate: true,
  license: {
    name: 'Public domain',
    url: 'https://www.fsa.usda.gov/help/policies-and-links/',
  },
  vendor: 'USDA',
};

LAYERS[PLANET_FOREST_DILIGENCE_PACKAGE] = {
  type: 'image',
  key: PLANET_FOREST_DILIGENCE_PACKAGE,
  resolutionCategory: 'data',
  display: 'Forest Carbon Diligence Package (30m)',
  shortName: 'Forest Carbon Diligence Package',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Planet%20Diligence%20(3m)%20Carbon',
  description: (
    <>
      Planet's Forest Carbon Diligence Package provides insight into historic forest conditions with
      annual 30m data for aboveground live carbon, canopy cover, and canopy height back to 2013,
      anywhere in the world. This data bundle can be purchased in the order pane for individual
      properties at $0.04 per acre (or $0.10 per hectare). Created using deep learning models
      trained on airborne lidar and multi-sensor satellite fusion (Landsat 8, Sentinel-2, and
      ALOS-PALSAR), this archive package supports evaluating trends of forest change and project
      baselining.
      <div style={{marginTop: '1rem'}}>
        <a
          href="https://docs.planet.com/data/planetary-variables/forest-carbon-diligence/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Learn more about the methodology here.
        </a>
      </div>
    </>
  ),
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(PLANET_FOREST_DILIGENCE_PACKAGE),
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[PLANET_FOREST_CANOPY_COVER] = {
  type: 'data',
  key: PLANET_FOREST_CANOPY_COVER,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'salo',
  display: 'Planet Canopy Cover (30m)',
  shortName: 'Canopy Cover',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  gradientStops: PLANET_FOREST_CARBON_CANOPY_COVER_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/canopy-cover-30m.png',
  description:
    'Canopy Cover indicates the horizontal cover fraction occupied by tree canopies, measured as a percentage. An area with 0% canopy cover would have no trees at all, and 90+% would be a dense forest.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(PLANET_FOREST_CANOPY_COVER),
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[PLANET_FOREST_CANOPY_HEIGHT] = {
  type: 'data',
  key: PLANET_FOREST_CANOPY_HEIGHT,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'salo',
  display: 'Planet Canopy Height (30m)',
  shortName: 'Canopy Height',
  dataRange: [0, 28],
  dataLabels: ['0', '28+ meters'],
  dataUnit: 'm',
  gradientStops: PLANET_FOREST_CARBON_CANOPY_HEIGHT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Canopy%20Height%20(30m).png',
  description:
    'Canopy Height shows the distance between the ground and the top of the canopy. Canopy height is an indicator of aboveground biomass and the amount of foliage that may be consumed in a crown fire and is measured in meters.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(PLANET_FOREST_CANOPY_HEIGHT),
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[PLANET_FOREST_ABOVEGROUND_CARBON] = {
  type: 'data',
  key: PLANET_FOREST_ABOVEGROUND_CARBON,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 30,
  display: 'Planet Aboveground Carbon (30m)',
  shortName: 'Aboveground Carbon',
  dataRange: [0, 255],
  // This is used to limit the color map of the data to stop at 100, and have
  // all values above it appear as the same color. This custom coloring is done on the backend
  // most of the time, but we handle coloring it here when using overlay masks.
  colorMapDataRange: [0, 100],
  dataLabels: ['0 MgC/ha', '100+ MgC/ha'],
  dataUnit: 'MgC',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Aboveground%20Carbon%20(30m).png',
  description:
    'The Aboveground Carbon layer shows aggregate annual carbon stocks in a forested area. This layer calculates carbon at 30m resolution in megagrammes, or tons, and can be used to assess project baselines and track carbon sequestration over time. ',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(PLANET_FOREST_ABOVEGROUND_CARBON),
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
};

LAYERS[PLANET_FOREST_MONITORING_PACKAGE] = {
  type: 'image',
  key: PLANET_FOREST_MONITORING_PACKAGE,
  resolutionCategory: 'data',
  display: 'Forest Carbon Monitoring Package (3m)',
  shortName: 'Forest Carbon Monitoring Package',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Forest%20Carbon%20Monitoring%20(3m).png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/planet-forest-carbon-package-highres.png',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: (startDate) => {
    const endDate = new Date(startDate);
    endDate.setMonth(startDate.getMonth() + 3);
    endDate.setDate(startDate.getDate() + 1);
    return [startDate, endDate];
  },
  license: {
    name: 'Planet License Agreement',
    url: 'https://assets.planet.com/docs/Master_Content_License_Agreement_2022.2.pdf',
  },
  vendor: 'Planet',
  description: (
    <>
      Planet's Forest Carbon Monitoring Package offers 3m data on canopy height, cover, and
      aboveground live carbon stocks to track forest changes at a granular level. With global
      coverage and quarterly updates, this dataset allows for more frequent monitoring of forest
      conditions and project tracking. The data is generated using advanced machine learning
      algorithms trained on a variety of satellite sources including Planet SkySat. Add this package
      and purchase for your properties at $0.10 per acre (or $0.25 per hectare) through the order
      pane.
      <a
        href="https://docs.planet.com/data/planetary-variables/forest-carbon-monitoring/"
        target="_blank"
        rel="noreferrer"
      >
        {' '}
        Learn more about this data here.
      </a>
    </>
  ),
};

LAYERS[PLANET_FOREST_ABOVEGROUND_CARBON_3M] = {
  type: 'data',
  key: PLANET_FOREST_ABOVEGROUND_CARBON_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  display: 'Planet Aboveground Carbon (3m)',
  shortName: 'Aboveground Carbon',
  dataRange: [0, 255],
  // This is used to limit the color map of the data to stop at 100, and have
  // all values above it appear as the same color. This custom coloring is done on the backend
  // most of the time, but we handle coloring it here when using overlay masks.
  colorMapDataRange: [0, 100],
  dataLabels: ['0 MgC/ha', '100+ MgC/ha'],
  dataUnit: 'MgC',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Aboveground%20Carbon%20(30m).png',
  description:
    'The Aboveground Carbon layer shows aggregate annual carbon stocks in a forested area. This layer calculates carbon at 3m resolution in megagrammes, or tons, and can be used to assess project baselines and track carbon sequestration over time. ',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].mosaic,
  license: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].license,
  vendor: 'Planet',
};
LAYERS[PLANET_FOREST_CANOPY_COVER_3M] = {
  type: 'data',
  key: PLANET_FOREST_CANOPY_COVER_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  display: 'Planet Canopy Cover (3m)',
  shortName: 'Canopy Cover',
  dataRange: [0, 100],
  dataLabels: ['0%', '100%'],
  dataUnit: '%',
  gradientStops: PLANET_FOREST_CARBON_CANOPY_COVER_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/canopy-cover-30m.png',
  description:
    'Canopy Cover indicates the horizontal cover fraction occupied by tree canopies, measured as a percentage. An area with 0% canopy cover would have no trees at all, and 90+% would be a dense forest.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].mosaic,
  license: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].license,
  vendor: 'Planet',
};
LAYERS[PLANET_FOREST_CANOPY_HEIGHT_3M] = {
  type: 'data',
  key: PLANET_FOREST_CANOPY_HEIGHT_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  display: 'Planet Canopy Height (3m)',
  shortName: 'Canopy Height',
  dataRange: [0, 28],
  dataLabels: ['0', '28+ meters'],
  dataUnit: 'm',
  gradientStops: PLANET_FOREST_CARBON_CANOPY_HEIGHT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Canopy%20Height%20(30m).png',
  description:
    'Canopy Height shows the distance between the ground and the top of the canopy. Canopy height is an indicator of aboveground biomass and the amount of foliage that may be consumed in a crown fire and is measured in meters.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].mosaic,
  license: LAYERS[PLANET_FOREST_MONITORING_PACKAGE].license,
  vendor: 'Planet',
};

LAYERS[SALO_ABOVEGROUND_CARBON_30M] = {
  type: 'data',
  key: SALO_ABOVEGROUND_CARBON_30M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 30,
  display: 'Aboveground Carbon (30m)',
  shortName: 'Aboveground Carbon',
  dataRange: [0, 250],
  dataLabels: ['0 MgC/ha', '100+ MgC/ha'],
  dataUnit: 'MgC/ha',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Aboveground%20Carbon%20(30m).png',
  description:
    'The Aboveground Carbon layer shows aggregate annual carbon stocks in a forested area. This layer calculates carbon at 30m resolution in megagrammes, or tons, and can be used to assess project baselines and track carbon sequestration over time. ',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_ABOVEGROUND_CARBON_30M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[SALO_CANOPY_COVER_30M] = {
  type: 'data',
  key: SALO_CANOPY_COVER_30M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'salo',
  resolution: 30,
  display: 'Canopy Cover (30m)',
  shortName: 'Canopy Cover',
  dataRange: [0, 90],
  dataLabels: ['0%', '90+%'],
  dataUnit: '%',
  gradientStops: SALO_GREEN_VEGETATION_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/canopy-cover-30m.png',
  description:
    'Canopy Cover indicates the horizontal cover fraction occupied by tree canopies, measured as a percentage. An area with 0% canopy cover would have no trees at all, and 90+% would be a dense forest.',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_CANOPY_COVER_30M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[SALO_CANOPY_HEIGHT_30M] = {
  type: 'data',
  key: SALO_CANOPY_HEIGHT_30M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Canopy Height (30m)',
  shortName: 'Canopy Height',
  resolution: 30,
  dataRange: [0, 20],
  dataLabels: ['0', '20+ meters'],
  dataUnit: 'm',
  gradientStops: SALO_GREEN_VEGETATION_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Canopy%20Height%20(30m).png',
  description:
    'Canopy Height shows the distance between the ground and the top of the canopy. Canopy height is an indicator of aboveground biomass and the amount of foliage that may be consumed in a crown fire and is measured in meters.',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_CANOPY_HEIGHT_30M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[SALO_ABOVEGROUND_CARBON_3M] = {
  type: 'data',
  key: SALO_ABOVEGROUND_CARBON_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  display: 'Aboveground Carbon (3m)',
  shortName: 'Aboveground Carbon',
  dataRange: [0, 250],
  dataLabels: ['0 MgC/ha', '100+ MgC/ha'],
  dataUnit: 'MgC/ha',
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Aboveground%20Carbon%20(30m).png',
  description:
    'The Aboveground Carbon layer shows aggregate annual carbon stocks in a forested area. This layer calculates carbon at 30m resolution in megagrammes, or tons, and can be used to assess project baselines and track carbon sequestration over time. ',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_ABOVEGROUND_CARBON_3M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[SALO_CANOPY_COVER_3M] = {
  type: 'data',
  key: SALO_CANOPY_COVER_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  indexCategory: 'salo',
  display: 'Canopy Cover (3m)',
  shortName: 'Canopy Cover',
  dataRange: [0, 90],
  dataLabels: ['0%', '90+%'],
  dataUnit: '%',
  gradientStops: SALO_GREEN_VEGETATION_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/canopy-cover-30m.png',
  description:
    'Canopy Cover indicates the horizontal cover fraction occupied by tree canopies, measured as a percentage. An area with 0% canopy cover would have no trees at all, and 90+% would be a dense forest.',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_CANOPY_COVER_3M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[SALO_CANOPY_HEIGHT_3M] = {
  type: 'data',
  key: SALO_CANOPY_HEIGHT_3M,
  hasRawLayer: true,
  resolutionCategory: 'data',
  resolution: 3,
  display: 'Canopy Height (3m)',
  shortName: 'Canopy Height',
  dataRange: [0, 20],
  dataLabels: ['0', '20+ meters'],
  dataUnit: 'm',
  gradientStops: SALO_GREEN_VEGETATION_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Canopy%20Height%20(30m).png',
  description:
    'Canopy Height shows the distance between the ground and the top of the canopy. Canopy height is an indicator of aboveground biomass and the amount of foliage that may be consumed in a crown fire and is measured in meters.',
  tags: [],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SALO_CANOPY_HEIGHT_3M),
  license: {
    name: 'Salo Sciences End User License',
    url: 'https://salo.ai/carbon-eula',
  },
  vendor: 'Salo Sciences',
};

LAYERS[CHLORIS_ABOVEGROUND_WOODY_BIOMASS] = {
  type: 'data',
  key: CHLORIS_ABOVEGROUND_WOODY_BIOMASS,
  resolutionCategory: 'data',
  display: 'Chloris Biomass (4.6km)',
  shortName: 'Chloris Biomass',
  dataRange: [0, 250],
  dataLabels: ['0', '750k tons'],
  gradientStops: VIRIDIS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Aboveground%20Carbon%20(30m).png',
  description:
    'The Chloris Global Biomass data estimates tons of aboveground biomass carbon stocks for terrestrial woody vegetation ecosystems using NASA MODIS data. Annual data is available from 2003 to 2019 at 4.6 km spatial resolution across the globe. Data is not available for areas without woody vegetation, such as Greenland and the Sahara Desert. This layer is ideal for getting a rough sense how biomass carbon storage has changed over recent decades.',
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_ABOVEGROUND_WOODY_BIOMASS),
  license: {
    name: 'Creative Commons Attribution Non Commercial Share Alike 4.0 International',
    url: 'https://spdx.org/licenses/CC-BY-NC-SA-4.0.html',
  },
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_BIOMASS_STOCK] = {
  type: 'data',
  key: CHLORIS_BIOMASS_STOCK,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Biomass Stock',
  shortName: 'Biomass Stock',
  dataRange: [0, 500],
  dataLabels: ['0 MgC', '500+ MgC'],
  dataUnit: 'MgC',
  gradientStops: CHLORIS_BIOMASS_STOCK_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/under_construction.jpg',
  description: '',
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_BIOMASS_STOCK),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_FOREST_COVER] = {
  type: 'image',
  key: CHLORIS_FOREST_COVER,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Forest Cover',
  shortName: 'Forest Cover',
  layerLegendMap: CHLORIS_FOREST_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(CHLORIS_FOREST_VALUE_MAP, options)),
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/under_construction.jpg',
  description: '',
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_FOREST_COVER),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_30_PACKAGE] = {
  type: 'image',
  key: CHLORIS_30_PACKAGE,
  resolutionCategory: 'data',
  display: 'Forest Carbon Package (30m)',
  shortName: 'Forest Carbon Package (30m)',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-30-carbon-stock.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/chloris-30-carbon-stock-highres.png',
  description: (
    <>
      <p>
        The Chloris Geospatial 30m Biomass and Forest Cover Package provides annual data on
        above-ground biomass stock and forest cover, with coverage back to 2000. This data bundle
        can be purchased in the order pane for individual properties at $0.04/acre, and the entire
        archive of annual data is included at no extra cost. The data bundle includes biomass stock,
        biomass stock change, and forest cover.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_30_PACKAGE),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_30_BIOMASS_STOCK] = {
  type: 'data',
  key: CHLORIS_30_BIOMASS_STOCK,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Biomass Stock (30m)',
  shortName: 'Biomass Stock',
  dataRange: [0, 500],
  dataLabels: ['0 MgC', '500+ MgC'],
  dataUnit: 'MgC',
  gradientStops: CHLORIS_BIOMASS_STOCK_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-30-carbon-stock.png',
  description: (
    <>
      <p>
        Above-Ground Biomass Stock provides annual estimates of dry biomass stock in live woody
        vegetation. It is designed to measure biomass losses from deforestation and degradation, and
        biomass gains from vegetation growth, at the 30m resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_30_BIOMASS_STOCK),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_30_BIOMASS_STOCK_CHANGE] = {
  type: 'data',
  key: CHLORIS_30_BIOMASS_STOCK_CHANGE,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Biomass Stock Change (30m)',
  shortName: 'Biomass Stock Change (MgC/ha)',
  dataRange: [-350, 350],
  dataLabels: ['-350 MgC/ha', '350 MgC/ha'],
  dataUnit: 'MgC/ha',
  gradientStops: SEISMIC_R_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-30-carbon-stock.png',
  description: (
    <>
      <p>
        Above-Ground Biomass Change provides annual estimates of dry biomass stock change in live
        woody vegetation. It is designed to measure biomass losses from deforestation and
        degradation, and biomass gains from vegetation growth, at the 30m resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_30_BIOMASS_STOCK_CHANGE),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_30_FOREST_COVER] = {
  type: 'image',
  key: CHLORIS_30_FOREST_COVER,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Forest Cover (30m)',
  shortName: 'Forest Cover',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/under_construction.jpg',
  description: (
    <>
      <p>
        Forest Cover is based on above-ground biomass thresholds and dynamics over time. It includes
        disaggregated reporting on degradation, deforestation, and forest area gains, at the 30m
        resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_30_FOREST_COVER),
  layerLegendMap: CHLORIS_FOREST_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(CHLORIS_FOREST_VALUE_MAP, options)),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_10_PACKAGE] = {
  type: 'image',
  key: CHLORIS_10_PACKAGE,
  resolutionCategory: 'data',
  display: 'Forest Carbon Package (10m)',
  shortName: 'Forest Carbon Package (10m)',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-10-package.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/chloris-10-package.png',
  description: (
    <>
      <p>
        The Chloris Geospatial 10m Biomass and Forest Cover Package provides annual data on
        above-ground biomass stock and forest cover, with coverage back to 2017. This data bundle
        can be purchased in the order pane for individual properties at $0.20/acre, and the entire
        archive of annual data is included at no extra cost. The data bundle includes biomass stock,
        biomass stock change, and forest cover.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_10_PACKAGE),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_10_BIOMASS_STOCK] = {
  type: 'data',
  key: CHLORIS_10_BIOMASS_STOCK,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Biomass Stock (10m)',
  shortName: 'Biomass Stock',
  dataRange: [0, 500],
  dataLabels: ['0 MgC', '500+ MgC'],
  dataUnit: 'MgC',
  gradientStops: CHLORIS_BIOMASS_STOCK_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-10-package.png',
  description: (
    <>
      <p>
        Above-Ground Biomass Stock provides annual estimates of dry biomass stock in live woody
        vegetation. It is designed to measure biomass losses from deforestation and degradation, and
        biomass gains from vegetation growth, at the 10m resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_10_BIOMASS_STOCK),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};

LAYERS[CHLORIS_10_BIOMASS_STOCK_CHANGE] = {
  type: 'data',
  key: CHLORIS_10_BIOMASS_STOCK_CHANGE,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Biomass Stock Change (10m)',
  shortName: 'Biomass Stock Change (MgC/ha)',
  gradientStops: SEISMIC_R_GRADIENT_STOPS,
  dataRange: [-350, 350],
  dataLabels: ['-350 MgC/ha', '350 MgC/ha'],
  dataUnit: 'MgC/ha',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-10-package.png',
  description: (
    <>
      <p>
        Above-Ground Biomass Change provides annual estimates of dry biomass stock change in live
        woody vegetation. It is designed to measure biomass losses from deforestation and
        degradation, and biomass gains from vegetation growth, at the 10m resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_10_BIOMASS_STOCK_CHANGE),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};
LAYERS[CHLORIS_10_FOREST_COVER] = {
  type: 'image',
  key: CHLORIS_10_FOREST_COVER,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Chloris Forest Cover (10m)',
  shortName: 'Forest Cover',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/chloris-10-package.png',
  description: (
    <>
      <p>
        Forest Cover is based on above-ground biomass thresholds and dynamics over time. It includes
        disaggregated reporting on degradation, deforestation, and forest area gains, at the 10m
        resolution.
      </p>
      <p>
        The data is generated using Chloris' proprietary ML and AI technology, which leverages
        biomass estimates from spaceborne and airborne LiDAR alongside earth observations, processed
        with a Bayesian time series approach to deliver robust, noise-filtered estimates of
        above-ground biomass and forest cover. Learn more about{' '}
        <a href="https://www.chloris.earth/key-documents" target="_blank" rel="noopener noreferrer">
          Chloris data methodology and validation here
        </a>
        {', '}
        and our{' '}
        <a
          href="https://lens.tech/support/article/170-chloris-forest-carbon"
          target="_blank"
          rel="noopener noreferrer"
        >
          support doc here
        </a>
        .
      </p>
    </>
  ),
  tags: [FOREST_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(CHLORIS_10_FOREST_COVER),
  layerLegendMap: CHLORIS_FOREST_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(getFilteredLayerLegendEntries(CHLORIS_FOREST_VALUE_MAP, options)),
  license: {
    name: 'Chloris Geospatial EULA',
    url: 'https://cdn.prod.website-files.com/6197d5aab3f545ea3c1c5438/671b9d5827701b056d11fa69_Chloris%20Geospatial%20AGB%20EULA.pdf',
  },
  resampling: 'nearest',
  vendor: 'Chloris Geospatial',
};

LAYERS[MOBI_SPECIES_RICHNESS_ALL] = {
  type: 'data',
  key: MOBI_SPECIES_RICHNESS_ALL,
  resolutionCategory: 'data',
  display: 'MoBI Species Richness (990m)',
  shortName: 'MoBI Species Richness',
  dataRange: [0, 14],
  dataLabels: ['0', '14 species'],
  gradientStops: MAGMA_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/MoBI%20Species%20Richness.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/MoBI%20Species%20Richness%20-%20HighRes.png',
  description:
    'The Map of Biodiversity Importance (MoBI) Species Richness Layer provides information about the number of species in the continental US that are protected by the Endangered Species Act and/or considered to be in danger of extinction. Species richness for all species includes vertebrates, freshwater invertebrates, pollinators, and vascular plants and the richness value represents the number of species with habitat overlapping a given 990 meter cell. As a synthesis of predictive models, they cannot guarantee either the presence or absence of imperiled species at a given location. This data is ideal for assisting in identifying areas that may be high priority for on-the-ground biodiversity conservation. NatureServe reserves all rights in all intellectual property provided. Distribution of the data or any intellectual property in whole or in part, or any products derived from the data or any intellectual property for commercial purposes is strictly prohibited.',
  tags: [FOREST_TAG, BIODIVERSITY_TAG],
  filter: CONUS_LAYER_FILTER,
  geographicExtent: CONTINENTAL_US_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(MOBI_SPECIES_RICHNESS_ALL),
  license: {
    name: 'NatureServe Terms and Conditions',
    url: 'https://www.natureserve.org/terms',
  },
  vendor: 'NatureServe',
};

LAYERS[GLOBAL_FOREST_COVER_FORESTCOVER] = {
  type: 'image',
  key: GLOBAL_FOREST_COVER_FORESTCOVER,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Global Forest Cover (10m)',
  shortName: 'Global Forest Cover',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/global-forest-cover-2020-v3-10m.png',
  mosaic: _getLayerMosaicDisplay(GLOBAL_FOREST_COVER_FORESTCOVER),
  layerLegendMap: GLOBAL_FOREST_COVER_FORESTCOVER_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(GLOBAL_FOREST_COVER_FORESTCOVER_VALUE_MAP, options)
    ),
  license: {name: 'None (Public Use)', url: ''},
  resampling: 'nearest',
  vendor: 'European Commission Joint Research Centre',
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  description: (
    <>
      The Global Map of Forest Cover for 2020 V3, developed by the European Commission's Joint
      Research Centre (JRC), provides a global classification of forest presence and absence at
      10-meter resolution. It aligns with the cut-off date of the EU regulation on
      deforestation-free products (EUDR) and reflects the relevant forest definition of land
      spanning more than 0.5 hectares with trees higher than 5 meters and a canopy cover of more
      than 10%, excluding urban, agricultural, and plantation land uses. The map integrates multiple
      global datasets on land cover, tree height, and land use to produce a detailed, spatially
      explicit snapshot of global forest cover as of 2020.{' '}
      <a href="https://forobs.jrc.ec.europa.eu/GFC" target="_blank" rel="noopener noreferrer">
        See here for more information.
      </a>
    </>
  ),
  tags: [VEGETATION_TAG, LANDCOVER_TAG, FOREST_TAG],
};
LAYERS[GLOBAL_FOREST_CHANGE_LOSSYEAR] = {
  type: 'image',
  key: GLOBAL_FOREST_CHANGE_LOSSYEAR,
  hasRawLayer: true,
  resolutionCategory: 'data',
  display: 'Global Forest Loss (30m)',
  shortName: 'Global Forest Loss',
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  layerLegendMap: GLOBAL_FOREST_CHANGE_LOSSYEAR_VALUE_MAP,
  renderLayerLegend: (options) => (
    <div>
      {renderColorKey(
        getFilteredLayerLegendEntries(GLOBAL_FOREST_CHANGE_LOSSYEAR_VALUE_MAP, options)
      )}
    </div>
  ),
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/Global%20Forest%20Loss.png',
  description: (
    <>
      The University of Maryland Global Forest Change layer displays 30m data showing gross forest
      loss for each year from 2001 to 2023, derived from Landsat imagery. Areas where forest has
      transitioned to non-forest during a given year are displayed in red on the map. Forest loss
      could be a result of natural or human-induced causes. This dataset makes it possible to assess
      the timing and annual extent of forest loss over the last two decades, which can support site
      assessments and baselining. Data and methodology were originally{' '}
      <a
        href="https://www.science.org/doi/10.1126/science.1244693"
        target="_blank"
        rel="noopener noreferrer"
      >
        published
      </a>{' '}
      by Hansen et. al and this work is a collaboration between the Global Land Analysis & Discovery
      Lab at the University of Maryland, Google, USGS, and NASA.
    </>
  ),
  tags: [VEGETATION_TAG, FOREST_TAG],
  mosaic: _getLayerMosaicDisplay(GLOBAL_FOREST_CHANGE_LOSSYEAR),
  license: {
    name: 'Creative Commons Attribution 4.0 International (CC BY 4.0)',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  resampling: 'nearest',
  vendor: 'University of Maryland',
};

LAYERS[IMPACT_OBSERVATORY_BIODIVERSITY] = {
  type: 'data',
  key: IMPACT_OBSERVATORY_BIODIVERSITY,
  resolutionCategory: 'data',
  hasRawLayer: true,
  display: 'Biodiversity Intactness (100m)',
  shortName: 'Biodiversity Intactness',
  dataRange: [0, 1],
  dataLabels: ['', 'Intact'],
  gradientStops: GREENS_GRADIENT_STOPS,
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/impact-observatory-biodiversity_thumb.png',
  highResPreview:
    'https://storage.googleapis.com/upstream-icons/layers/impact-observatory-biodiversity_banner.png',
  description: (
    <>
      This data depicts the terrestrial intactness of biodiversity globally for the years 2017-2020
      at a 100-meter resolution. The intactness of biodiversity is based both on the abundance of
      individual species as well the species composition compared to an intact baseline. This
      dataset can help identify areas of critical remaining intact habitat and support spatial
      planning and management. Learn more about the methodology in{' '}
      <a
        href="https://ai4edatasetspublicassets.blob.core.windows.net/assets/pdfs/io-biodiversity/Biodiversity_Intactness_whitepaper.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        this white paper
      </a>
      .
    </>
  ),
  tags: [FOREST_TAG, BIODIVERSITY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(IMPACT_OBSERVATORY_BIODIVERSITY),
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  vendor: 'Impact Observatory',
};

LAYERS[SPACE_INTELLIGENCE_CARBON] = {
  type: 'data',
  key: SPACE_INTELLIGENCE_CARBON,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'space-intelligence',
  display: 'CarbonMapper (25m)',
  shortName: 'CarbonMapper',
  dataRange: [0, 250],
  dataLabels: ['0 MgC/ha', '250+ MgC/ha'],
  dataUnit: 'MgC',
  gradientStops: SPACE_INTELLIGENCE_ABOVEGROUND_CARBON_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/CarbonMapper%20(25m).png',
  description:
    'CarbonMapper estimates per-pixel biomass calculations for carbon stocks to inform carbon storage over time.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SPACE_INTELLIGENCE_CARBON),
  vendor: 'Space Intelligence',
};

LAYERS[SPACE_INTELLIGENCE_CARBON_TREE_CANOPY] = {
  type: 'data',
  key: SPACE_INTELLIGENCE_CARBON_TREE_CANOPY,
  resolutionCategory: 'data',
  indexCategory: 'space-intelligence',
  display: 'CarbonMapper: Tree Carbon (0.1m)',
  shortName: 'CarbonMapper',
  dataRange: [0, 8500],
  dataLabels: ['0 kgC/tree', '8,500+ kgC/tree'],
  gradientStops: SPACE_INTELLIGENCE_ABOVEGROUND_CARBON_GRADIENT_STOPS,
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/CarbonMapper%20(25m).png',
  description: 'This CarbonMapper layer shows the kg of carbon per tree.',
  tags: [FOREST_TAG, VEGETATION_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SPACE_INTELLIGENCE_CARBON_TREE_CANOPY),
  vendor: 'Space Intelligence',
};

LAYERS[SPACE_INTELLIGENCE_HABITAT] = {
  type: 'image',
  key: SPACE_INTELLIGENCE_HABITAT,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'space-intelligence',
  display: 'HabitatMapper (25m)',
  shortName: 'HabitatMapper',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/HabitatMapper%20(25m).png',
  layerLegendMap: SPACE_INTELLIGENCE_HABITAT_VALUE_MAP,
  renderLayerLegend: (options) => (
    <div>
      <p>
        This layer shows{' '}
        <a href="https://www.space-intelligence.com/habitatmapper-landcover-mapping/">
          Space Intelligence's HabitatMapper™
        </a>{' '}
        classifications of land cover.
      </p>
      {renderColorKey(getFilteredLayerLegendEntries(SPACE_INTELLIGENCE_HABITAT_VALUE_MAP, options))}
    </div>
  ),
  description:
    'HabitatMapper provides landcover classifications to support assessments of land use change over time.',
  tags: [LANDCOVER_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SPACE_INTELLIGENCE_HABITAT),
  resampling: 'nearest',
  vendor: 'Space Intelligence',
};

LAYERS[SPACE_INTELLIGENCE_HABITAT_URBAN] = {
  type: 'image',
  key: SPACE_INTELLIGENCE_HABITAT_URBAN,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'space-intelligence',
  display: 'HabitatMapper Urban Classes (25m)',
  shortName: 'HabitatMapper (Urban Classes)',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/HabitatMapper%20(25m).png',
  layerLegendMap: SPACE_INTELLIGENCE_HABITAT_URBAN_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(SPACE_INTELLIGENCE_HABITAT_URBAN_VALUE_MAP, options)
    ),
  description:
    'HabitatMapper provides landcover classifications to support assessments of land use change over time.',
  tags: [LANDCOVER_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SPACE_INTELLIGENCE_HABITAT_URBAN),
  resampling: 'nearest',
  vendor: 'Space Intelligence',
};

LAYERS[SPACE_INTELLIGENCE_HABITAT_10_CLASS] = {
  type: 'image',
  key: SPACE_INTELLIGENCE_HABITAT_10_CLASS,
  hasRawLayer: true,
  resolutionCategory: 'data',
  indexCategory: 'space-intelligence',
  display: 'Landcover (Space Intelligence)',
  resolution: 10,
  shortName: 'Landcover',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/HabitatMapper%20(10m).png',
  layerLegendMap: SPACE_INTELLIGENCE_HABITAT_10_CLASS_VALUE_MAP,
  renderLayerLegend: (options) =>
    renderColorKey(
      getFilteredLayerLegendEntries(SPACE_INTELLIGENCE_HABITAT_10_CLASS_VALUE_MAP, options)
    ),
  description: (
    <>
      Space Intelligence landcover data, or HabitatMapper, provides country-specific land cover
      data, enabling project developers and investors to inform and validate forest carbon project
      baselines. By offering local calibration, the dataset captures regional factors for enhanced
      precision, allowing users to screen areas, review project activity, and perform due diligence.
      Delivered through Lens and as GeoTIFF files for requested areas, this data helps monitor
      habitat composition and changes over time. Trusted in the voluntary carbon market,
      HabitatMapper supports critical baseline assumptions and impact measurements for forest
      conservation projects.
      <a
        href="https://lens.tech/support/article/172-space-intelligence"
        target="_blank"
        rel="noreferrer"
      >
        {' '}
        Learn more about available geographies and pricing here{' '}
      </a>
      or email us at <a href="mailto:lens@upstream.tech">lens@upstream.tech</a> to request data for
      your areas of interest.
    </>
  ),
  tags: [LANDCOVER_TAG],
  geographicExtent: CUSTOM_GEOGRAPHIC_EXTENT,
  mosaic: _getLayerMosaicDisplay(SPACE_INTELLIGENCE_HABITAT_10_CLASS),
  license: {
    name: 'Space Intelligence License Agreement',
    url: 'https://142260897.fs1.hubspotusercontent-eu1.net/hubfs/142260897/Legal%20Documents/Space%20Intelligence%20EULA.pdf',
  },
  resampling: 'nearest',
  libraryAction: {
    text: 'Request',
    link: 'https://form.asana.com/?k=enNwaZ1O-_S01_P5R5caYw&d=253187955686276',
  },
  vendor: 'Space Intelligence',
};

LAYERS[PRISM_M_PRECIPITATION] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: PRISM_M_PRECIPITATION,
  hasRawLayer: true,
  dataUnit: 'in',
  mosaic: _getLayerMosaicDisplay(PRISM_M_PRECIPITATION),
  // PRISM serves the data in mm. Below we are converting a dataRange of 0-1016mm to inches
  // to make this more understandable for users.
  dataRange: [0, 40],
  dataLabels: ['0 in', '40+ in'],
  display: 'Precipitation (PRISM)',
  shortName: 'Precipitation',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/PRISM_precip.png',
  gradientStops: COOL_GRADIENT_STOPS,
  isNotDisplayedOnMap: true,
  description: (
    <>
      Monthly precipitation data from the PRISM Climate Group at Oregon State University offers
      insights into monthly patterns across the United States. The PRISM data uses a weighted
      regression scheme to account for complex climate regimes associated with orography, rain
      shadows, temperature inversions, slope aspect, coastal proximity, and other factors. By
      assimilating station data from various networks nationwide, PRISM provides a detailed
      understanding of precipitation which can be used to contextualize other Lens datasets,
      identify risks such as drought conditions, and support water resource management decisions.{' '}
      <a
        href="https://www.prism.oregonstate.edu/documents/PRISM_datasets.pdf"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more about PRISM datasets here
      </a>
      .
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: US_GEOGRAPHIC_EXTENT,
  vendor: 'PRISM Climate Group',
};

LAYERS[GEFS_ANALYSIS_MONTHLY_PRECIPITATION] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: GEFS_ANALYSIS_MONTHLY_PRECIPITATION,
  hasRawLayer: true,
  dataUnit: 'mm',
  mosaic: _getLayerMosaicDisplay(GEFS_ANALYSIS_MONTHLY_PRECIPITATION),
  dataRange: [0, 1016],
  dataLabels: ['0 mm', '1016+ mm'],
  display: 'Precipitation (GEFS Analysis)',
  shortName: 'Precipitation',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Precipitation%20(GEFS%20Analysis%20Monthly).png',
  gradientStops: COOL_GRADIENT_STOPS,
  isNotDisplayedOnMap: true,
  description: (
    <>
      <p>
        Monthly aggregated precipitation data from dynamical.org and NOAA. The Global Ensemble
        Forecast System (GEFS) is a National Oceanic and Atmospheric Administration (NOAA) National
        Centers for Environmental Prediction (NCEP) weather forecast model. GEFS creates 31 separate
        forecasts (ensemble members) to describe the range of forecast uncertainty.
      </p>
      <p>
        This analysis dataset is an archive of the model's best estimate of past weather. It is
        created by concatenating the first few hours of each historical forecast.
      </p>
      <a
        href="https://dynamical.org/catalog/noaa-gefs-analysis/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more about the GEFS analysis dataset here
      </a>
      .
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  vendor: 'NOAA/dynamical.org',
};

LAYERS[GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN] = {
  type: 'data',
  resolutionCategory: 'higher-frequency',
  indexCategory: 'index',
  key: GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN,
  hasRawLayer: true,
  dataUnit: '°C',
  mosaic: _getLayerMosaicDisplay(GEFS_ANALYSIS_MONTHLY_TEMPERATURE_MEAN),
  dataRange: [-40, 55],
  dataLabels: ['-40°C', '55°C'],
  display: 'Temperature Mean (GEFS Analysis)',
  shortName: 'Temperature Mean',
  thumbnailUrl:
    'https://storage.googleapis.com/upstream-icons/layers/Temperature%20Mean%20(GEFS%20Analysis%20Monthly).png',
  gradientStops: RDBU_R_GRADIENT_STOPS,
  isNotDisplayedOnMap: true,
  description: (
    <>
      <p>
        Monthly aggregated mean temperature data from dynamical.org and NOAA. The Global Ensemble
        Forecast System (GEFS) is a National Oceanic and Atmospheric Administration (NOAA) National
        Centers for Environmental Prediction (NCEP) weather forecast model. GEFS creates 31 separate
        forecasts (ensemble members) to describe the range of forecast uncertainty.
      </p>
      <p>
        This analysis dataset is an archive of the model's best estimate of past weather. It is
        created by concatenating the first few hours of each historical forecast.
      </p>
      <a
        href="https://dynamical.org/catalog/noaa-gefs-analysis/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Learn more about the GEFS analysis dataset here
      </a>
      .
    </>
  ),
  tags: [HYDROLOGY_TAG],
  geographicExtent: GLOBAL_GEOGRAPHIC_EXTENT,
  license: {
    name: 'Creative Commons Attribution 4.0 International',
    url: 'https://creativecommons.org/licenses/by/4.0/',
  },
  vendor: 'NOAA/dynamical.org',
};

LAYERS[GNATSGO_SOILTYPE] = {
  type: 'image',
  resolutionCategory: 'data',
  key: GNATSGO_SOILTYPE,
  hasRawLayer: true,
  mosaic: _getLayerMosaicDisplay(GNATSGO_SOILTYPE),
  display: 'Soil Hydrologic Group (USDA)',
  shortName: 'Soil Hydrologic Group',
  thumbnailUrl: 'https://storage.googleapis.com/upstream-icons/layers/gnatsgo_soiltype.png',
  highResPreview: 'https://storage.googleapis.com/upstream-icons/layers/gnatsgo_soiltype.png',
  layerLegendMap: GNATSGO_SOILTYPE_VALUE_MAP,
  renderLayerLegend: (options) =>
    // Don't sort the legend entries so they match the order of the values in the layer legend map.
    renderColorKey(
      getFilteredLayerLegendEntries(GNATSGO_SOILTYPE_VALUE_MAP, options),
      false,
      options?.includeExtendedDescription
    ),
  description: (
    <>
      The gridded National Soil Survey Geographic Database (gNATSGO) is a comprehensive dataset that
      combines the Soil Survey Geographic Database (SSURGO), State Soil Geographic Database
      (STATSGO2), and Raster Soil Survey Databases (RSS) into a single, gridded dataset. It is
      maintained by the United States Department of Agriculture (USDA) and Natural Resources
      Conservation Service (NRCS). It provides detailed information about soil properties and
      classifications across the United States.
    </>
  ),
  tags: [VEGETATION_TAG, LANDCOVER_TAG],
  geographicExtent: US_GEOGRAPHIC_EXTENT,
  license: {
    name: 'US Public Domain',
    url: 'https://creativecommons.org/public-domain/',
  },
  vendor: 'USDA',
  resampling: 'nearest',
};

export function renderColorKey(
  colorsAndLabels: {color: string; label: string; order?: number; extendedDescription?: string}[],
  sort = true,
  showExtendedDescription = false
) {
  if (sort) {
    colorsAndLabels = sortBy(colorsAndLabels, [
      (colorsAndLabel) => colorsAndLabel.order ?? Number.MAX_SAFE_INTEGER,
      'label',
    ]);
  }
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {colorsAndLabels.map(({color, label, extendedDescription}, i) => {
        const isExtendedDescription = showExtendedDescription && !!extendedDescription;

        return (
          <div
            key={`${label}-${color}-${i}`}
            style={{
              display: 'flex',
              justifyContent: 'flex-start',
              alignItems: 'flex-start',
              lineHeight: '20px',
            }}
          >
            <div
              style={{
                width: 10,
                height: 10,
                flexShrink: 0,
                marginTop: 5,
                backgroundColor: color,
                borderRadius: 2,
                ...(isWhite(color) && {
                  border: `1px solid ${colors.darkestGray}`,
                }),
              }}
            />
            <div
              style={{
                // Wrapped lines indent so a multi-line label reads as one entry; prose
                // descriptions are exempt, where that reads as a nested block.
                paddingLeft: isExtendedDescription ? 10 : 20,
                textIndent: isExtendedDescription ? 0 : -10,
                // minWidth lets the flex item shrink below its longest word, so break-word can act.
                minWidth: 0,
                overflowWrap: 'break-word',
              }}
            >
              {isExtendedDescription ? <span>{extendedDescription}</span> : label}
            </div>
          </div>
        );
      })}
    </div>
  );
}
